// Learn Now — pro split-screen lesson studio with switchable top-tab views.
// Left     : main-menu rail (icon, opens slide-out drawer) + lessons rail (collapsed-by-default, expands to chapter accordion)
// Centre   : top tab-bar switches between Lesson / Notes / Q&A / MagicDesk / Super Narrate / Deskeynomics
// Right    : AI Tutor / Notes (drag-resizable from its left edge)
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight as ChevronRightIcon, Send, MessagesSquare,
  NotebookPen, X, Menu, BookOpen, ListTree, Calendar, FolderOpen,
  Paperclip, FileText as FileTextIcon, Link2,
  BookText, StickyNote, HelpCircle, Sparkles, Mic, BookImage,
  ZoomIn, ZoomOut, Plus, Trash2, Type, Volume2, VolumeX,
  Image as ImageIcon, Eraser, RotateCcw, RotateCw, GripVertical, ThumbsUp,
} from 'lucide-react';
import { featureFill } from './lesson_views/featureColors';
import { TINT_PRIMARY } from '../studentUi';
import { LessonSkeleton } from '../StudentSkeletons';
import { InlineSparkle } from '../../../components/Loader';
import api, { notifyCreditsChanged } from '../studentApi';
import { useStudent } from '../StudentApp';
import LessonView from './lesson_views/LessonView';
import NotesView from './lesson_views/NotesView';
import QAView from './lesson_views/QAView';
import MagicDeskView from './lesson_views/MagicDeskView';
import SuperNarrateView from './lesson_views/SuperNarrateView';
import DeskeyVideoStudio from './lesson_views/DeskeyVideoStudio';
import AssistantBubble, { isTypingMuted, setTypingMuted } from './lesson_views/TutorBubble';
import ImageConfirmModal from './components/ImageConfirmModal';
import RechargeCreditsModal from './components/RechargeCreditsModal';
import ChatImageBubble from './components/ChatImageBubble';
import ModelPicker, { ModelBadge } from '../../../components/ModelPicker';
import ExamModeToggle, { ExamModeWave, ExamModeSparkBurst, ExamModeIntro, hasSeenExamIntro } from './components/ExamModeToggle';
import { getExamModeColors, getExamModeGoldGradient, getExamModePanelVars } from '../examPrepTheme';
import ReferencePopover, { appendReferencesToMessage } from '../../../components/ReferencePopover';
import InlineReferenceSuggest from '../../../components/InlineReferenceSuggest';
import { toast } from 'sonner';
import { showBadgeToast, showTrophyToast } from '../BadgeUnlockToast';
import InlineImagePrompt from './components/InlineImagePrompt';

// Chat slash-commands surfaced when the student types "/" in the composer —
// same interaction pattern as the Teacher Editor chat (COMMANDS there).
const CHAT_COMMANDS = [
  { cmd: '/Generate image', label: 'Make image', desc: "Turn the tutor's last explanation into a picture" },
];

import InlineImageBuilder from './components/InlineImageBuilder';
import { STICKY_SIZE, toStored, toPixels } from './stickyPlacement';
// App-wide nav shared with the StudentApp dashboard sidebar — one source of
// truth so the in-lesson menu can never drift from the dashboard again.
import { NAV_ITEMS, StudentSidebar } from '../StudentNav';
import { notifyError, friendlyError, MESSAGES, errorDetail } from '../../../lib/errors';
import { stripSectionMentions } from '../../../lib/chatMentions';
import { AttachmentStrip, UploadingTile } from '../../../components/chat/ChatAttachments';
import { confirmDialog } from '../../../components/ConfirmDialog';

// Top tab bar views. Each gets its own switchable page.
const VIEWS = [
  { id: 'lesson',       label: 'Lesson',        icon: BookText },
  { id: 'notes',        label: 'Notes',         icon: StickyNote },
  { id: 'qa',           label: 'Q&A',           icon: HelpCircle },
  { id: 'magicdesk',    label: 'MagicDesk',     icon: Sparkles,   tint: '#8B5CF6' },
  { id: 'narrate',      label: 'Super Narrate', icon: Mic,        tint: '#0EA5E9' },
  { id: 'deskeynomics', label: 'Video Studio',  icon: BookImage,  tint: '#F97316' },
];

const ZOOM_LEVELS = [0.8, 0.9, 1.0, 1.1, 1.25, 1.5];

// Right-side Tutor chat panel — resizable, but never so narrow that the
// composer's action row (symbols/attach/reference/model switcher/send)
// stops fitting on one line.
// 460, not 380: the composer's right-hand action row grew a third pill
// (Exam Mode toggle) alongside the model picker and send button — 380 was
// only ever wide enough for two.
const MIN_CHAT_WIDTH = 440;
const MAX_CHAT_WIDTH = 900;
const DEFAULT_CHAT_WIDTH = 440;
const clampChatWidth = (w) => Math.max(MIN_CHAT_WIDTH, Math.min(MAX_CHAT_WIDTH, w));

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
  catch { return iso.slice(0, 10); }
};

function groupByChapter(navList = []) {
  const map = new Map();
  for (const n of navList) {
    const key = n.chapter_id || n.chapter_label || 'general';
    if (!map.has(key)) map.set(key, { id: key, label: n.chapter_label || 'General Lessons', lessons: [] });
    map.get(key).lessons.push(n);
  }
  return [...map.values()];
}

// ═══════════════════════════════════════════════════════════════════════════
// Page
// ═══════════════════════════════════════════════════════════════════════════
export default function LearnNowPage() {
  const { lessonId } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  // "Discuss this wrong answer" entry point (StudentQuizAttempt's result
  // screen navigates here with router state). Consumed on the very next
  // send() call only, then cleared -- never resent on later turns.
  const pendingWrongAnswerRef = useRef(location.state?.wrongAnswerRef || null);
  const [navList, setNavList] = useState([]);
  const contentRef = useRef(null);
  // Cached per lesson: reopening one is instant. While a new lesson loads,
  // the previous one stays on screen (dimmed) instead of a blank page.
  const lessonQ = useQuery({
    queryKey: ['student', 'lesson', lessonId],
    queryFn: () => api.get(`/student/mydesk/lesson/${lessonId}`),
    placeholderData: keepPreviousData,
    meta: { silent: true },
  });
  const lesson = lessonQ.data?.lesson ?? null;
  const firstLoad = lessonQ.isPending;
  const switching = lessonQ.isPlaceholderData;
  const err = lessonQ.error ? friendlyError(lessonQ.error, 'Could not open this lesson') : null;
  const viewedLessonRef = useRef(null);

  const [lessonsOpen, setLessonsOpen] = useState(false);
  const [mainMenuOpen, setMainMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(true);
  const [chatWidth, setChatWidth] = useState(() => {
    const saved = +(localStorage.getItem('learn_now_chat_width') || 0);
    return saved ? clampChatWidth(saved) : DEFAULT_CHAT_WIDTH;
  });
  const [tab, setTab] = useState('ask');

  // Which tab-bar view is active in the center
  const [view, setView] = useState('lesson');
  const [zoomIdx, setZoomIdx] = useState(2);   // 1.0rem default

  // Active MagicDesk generation id — set by MagicDeskView when the student
  // opens a saved or freshly-generated retune. Used to route the right-pane
  // Tutor chat to the MagicDesk-grounded chat endpoint so the SAME chat
  // window handles doubts on both the original lesson and the retuned one.
  const [magicdeskGenId, setMagicdeskGenId] = useState(null);
  // Reset whenever the lesson changes or the student leaves the MagicDesk
  // tab — keeps the chat anchored to the right context, never stale.
  useEffect(() => { setMagicdeskGenId(null); }, [lessonId]);
  useEffect(() => { if (view !== 'magicdesk') setMagicdeskGenId(null); }, [view]);

  // Once per lesson shown (cached or fresh): keep the nav list, scroll to the
  // top and count the view.
  const shown = lessonQ.isPlaceholderData ? null : lessonQ.data;
  useEffect(() => {
    if (!shown || viewedLessonRef.current === lessonId) return;
    viewedLessonRef.current = lessonId;
    if (Array.isArray(shown.nav) && shown.nav.length) setNavList(shown.nav);
    if (contentRef.current) contentRef.current.scrollTop = 0;
    // Each lesson opened counts as a topic viewed today (Mileage bonus).
    api.post('/student/badges/event', {
      event_type: 'topic_view', topic: shown.lesson?.title, subject: shown.lesson?.subject,
      payload: { lesson_id: lessonId },
    }).catch(() => {});
  }, [shown, lessonId]);

  // Reset back to Lesson tab when switching lessons (fresh perspective)
  useEffect(() => { setView('lesson'); }, [lessonId]);

  // Mastery badges: a lesson counts as completed once the student scrolls its
  // LAST section into view. The backend dedupes per lesson id, so re-reading
  // never double counts; we also send at most once per lesson load.
  useEffect(() => {
    if (!lesson?.id || view !== 'lesson' || typeof IntersectionObserver === 'undefined') return;
    const root = contentRef.current;
    const sections = root?.querySelectorAll('[data-testid="lesson-view"] section[id^="student-pv-sec-"]');
    const last = sections && sections[sections.length - 1];
    if (!last) return;
    let sent = false;
    const io = new IntersectionObserver((entries) => {
      if (sent || !entries.some(e => e.isIntersecting)) return;
      sent = true;
      io.disconnect();
      api.post('/student/badges/event', {
        event_type: 'lesson_completed', topic: lesson.title, subject: lesson.subject,
        payload: { lesson_id: lesson.id },
      }).then(r => {
        const earned = Array.isArray(r?.newly_earned) ? r.newly_earned : [];
        earned.filter(x => !x.trophy_unlocked).forEach(showBadgeToast);
        earned.filter(x => x.trophy_unlocked).forEach(x => showTrophyToast(x.trophy_unlocked));
      }).catch(() => {});
    }, { root, threshold: 0.1 });
    io.observe(last);
    return () => io.disconnect();
  }, [lesson?.id, view]);

  // Auto-close the Tutor chat panel when the student switches to a
  // generation tab (MagicDesk / Super Narrate / Video Studio) — those views
  // need the full width and have their own credit/generation chrome. The
  // student can still reopen chat manually via its toggle on any tab.
  useEffect(() => {
    if (view === 'magicdesk' || view === 'narrate' || view === 'deskeynomics') setChatOpen(false);
  }, [view]);

  const chapters = useMemo(() => groupByChapter(navList), [navList]);

  // Notes — a stack of blocks (text paragraphs + sticky callouts) so the
  // student can pin coloured sticky boxes inside the same notebook. Lives
  // here (not in TutorPane) so a sticky can also be placed directly on the
  // Lesson content, which is a sibling of TutorPane, not a descendant.
  // Stored as JSON under a v2 key; the old plain-string key is migrated once.
  const [noteBlocks, setNoteBlocks] = useState(() => loadNoteBlocks(lessonId));
  // Guards the save effect below against writing session A's still-in-state
  // blocks under session B's key: on a lessonId change both effects fire in
  // the same flush, save-before-reload, so without this the save effect would
  // persist stale blocks under the new lesson's key right before the reload
  // effect reads them back — showing (and permanently storing) A's sticky
  // under B. Skip exactly one save right after a lesson switch.
  const skipNextSave = useRef(true);
  useEffect(() => {
    skipNextSave.current = true;
    setNoteBlocks(loadNoteBlocks(lessonId));
  }, [lessonId]);
  useEffect(() => {
    if (skipNextSave.current) { skipNextSave.current = false; return; }
    localStorage.setItem(`mydesk_notes_v2_${lessonId}`, JSON.stringify(noteBlocks));
  }, [noteBlocks, lessonId]);
  // Bounding box a sticky can be dropped onto to live directly on the lesson
  // content (see StickyBlock's grip handle and CanvasSticky).
  const lessonCanvasRef = useRef(null);

  if (firstLoad && !lesson) return <LessonSkeleton />;
  if (err && !lesson) return (
    <div className="py-20 text-center text-sm text-red-500">
      {err}
      <div className="mt-3"><Link to="/student/mydesk" className="underline">Back to MyDesk</Link></div>
    </div>
  );
  if (!lesson) return null;

  return (
    <div className="flex h-[calc(100vh-64px)]" data-testid="learn-now-page">
      <MainMenuRail onOpen={() => setMainMenuOpen(true)} />
      <LessonsSidebar
        open={lessonsOpen}
        onToggle={() => setLessonsOpen(v => !v)}
        chapters={chapters}
        activeId={lesson.id}
        onPick={(id) => nav(`/student/mydesk/lesson/${id}`)}
        backHref={`/student/mydesk/class/${lesson.class_id}`}
      />

      <main ref={contentRef} className="flex-1 min-w-0 overflow-auto relative" data-testid="learn-now-content">
        {switching && (
          <div className="absolute top-0 left-0 right-0 h-0.5 overflow-hidden z-20" data-testid="learn-now-switching">
            <div className="h-full animate-pulse" style={{ background: 'var(--st-primary)', width: '40%' }} />
          </div>
        )}

        {/* ── Top tab bar (per-lesson switchable views) ──────────── */}
        <TopTabBar view={view} onChange={setView} zoomIdx={zoomIdx} setZoomIdx={setZoomIdx} />

        <div key={`${lesson.id}-${view}`} className={`transition-opacity duration-150 ${switching ? 'opacity-60' : 'opacity-100'}`}>
          {view === 'lesson' && (
            // overflow-x: clip — nothing on the lesson (e.g. a note's corner)
            // can widen it into a sideways-scrolling page.
            <div ref={lessonCanvasRef} className="relative" style={{ overflowX: 'clip' }}>
              <LessonView lesson={lesson} zoom={ZOOM_LEVELS[zoomIdx]} />
              <CanvasStickyLayer blocks={noteBlocks} setBlocks={setNoteBlocks} lessonCanvasRef={lessonCanvasRef} />
            </div>
          )}
          {view === 'notes'        && <div className="px-6 sm:px-10 py-6"><NotesView lessonId={lesson.id} lessonTitle={lesson.title} /></div>}
          {view === 'qa'           && <div className="px-6 sm:px-10 py-6"><QAView lessonId={lesson.id} lessonTitle={lesson.title} classId={lesson.class_id} /></div>}
          {view === 'magicdesk'    && <div className="px-6 sm:px-10 py-6"><MagicDeskView lessonId={lesson.id} lessonTitle={lesson.title} onActiveGenChange={setMagicdeskGenId} /></div>}
          {view === 'narrate'      && <div className="px-6 sm:px-10 py-6"><SuperNarrateView lessonId={lesson.id} lessonTitle={lesson.title} /></div>}
          {view === 'deskeynomics' && <div className="px-6 sm:px-10 py-6"><DeskeyVideoStudio lessonId={lesson.id} lessonTitle={lesson.title} /></div>}
        </div>
      </main>

      <TutorPane
        lessonId={lesson.id}
        lessonTitle={lesson.title}
        sections={lesson.framework_sections || lesson.framework?.sections || []}
        view={view}
        magicdeskGenId={magicdeskGenId}
        open={chatOpen}
        setOpen={setChatOpen}
        width={chatWidth}
        setWidth={(w) => { setChatWidth(w); localStorage.setItem('learn_now_chat_width', String(w)); }}
        tab={tab}
        setTab={setTab}
        noteBlocks={noteBlocks}
        setNoteBlocks={setNoteBlocks}
        lessonCanvasRef={lessonCanvasRef}
        pendingWrongAnswerRef={pendingWrongAnswerRef}
      />

      <MainMenuDrawer open={mainMenuOpen} onClose={() => setMainMenuOpen(false)} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Top Tab Bar — sticky; per-view switch + zoom controls
// ═══════════════════════════════════════════════════════════════════════════
function TopTabBar({ view, onChange, zoomIdx, setZoomIdx }) {
  const zoomPct = Math.round(ZOOM_LEVELS[zoomIdx] * 100);
  return (
    <div className="sticky top-0 z-30 backdrop-blur border-b px-4 sm:px-6 py-2.5 flex items-center gap-2 flex-wrap"
      style={{ background: 'color-mix(in srgb, var(--st-card) 92%, transparent)', borderColor: 'var(--st-border)' }}
      data-testid="learn-now-tabs">
      <div className="flex items-center gap-1 flex-wrap">
        {VIEWS.map(v => {
          const a = view === v.id;
          const Icon = v.icon;
          return (
            <button key={v.id} onClick={() => onChange(v.id)}
              data-testid={`learn-now-tab-${v.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition"
              style={{
                background: a ? (v.tint ? featureFill(v.tint) : 'var(--st-primary)') : 'var(--st-bg)',
                color: a ? (v.tint ? '#fff' : 'var(--st-on-primary)') : 'inherit',
                border: `1px solid ${a ? (v.tint ? featureFill(v.tint) : 'var(--st-primary)') : 'var(--st-border)'}`,
              }}>
              <Icon className="w-3.5 h-3.5" />
              {v.label}
            </button>
          );
        })}
      </div>
      <div className="flex-1" />
      {view === 'lesson' && (
        <div className="inline-flex items-center gap-1 rounded-full border px-1 py-0.5" style={{ borderColor: 'var(--st-border)' }} data-testid="learn-now-zoom">
          <button onClick={() => setZoomIdx(i => Math.max(0, i - 1))} disabled={zoomIdx === 0}
            data-testid="learn-now-zoom-out"
            className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-black/5 disabled:opacity-30">
            <ZoomOut className="w-3.5 h-3.5" />
          </button>
          <span className="text-xs font-semibold tabular-nums px-1" style={{ color: 'var(--st-mute)' }}>{zoomPct}%</span>
          <button onClick={() => setZoomIdx(i => Math.min(ZOOM_LEVELS.length - 1, i + 1))} disabled={zoomIdx === ZOOM_LEVELS.length - 1}
            data-testid="learn-now-zoom-in"
            className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-black/5 disabled:opacity-30">
            <ZoomIn className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Main-menu rail + slide-out drawer
// ═══════════════════════════════════════════════════════════════════════════
function MainMenuRail({ onOpen }) {
  return (
    <div
      className="flex flex-col items-center py-3 border-r shrink-0"
      style={{ background: 'var(--st-card)', borderColor: 'var(--st-border)', width: 44 }}
      data-testid="learn-now-main-rail">
      <button onClick={onOpen}
        data-testid="learn-now-mainmenu-open"
        title="Open main menu"
        className="w-9 h-9 rounded-xl flex items-center justify-center text-white shadow-sm transition-transform "
        style={{ background: 'var(--st-primary)' }}>
        <Menu className="w-4 h-4" />
      </button>
      <div className="mt-3 h-px w-6" style={{ background: 'var(--st-border)' }} />
      <div className="mt-3 flex flex-col gap-1.5">
        {NAV_ITEMS.slice(0, 5).map(({ to, icon: Icon, label }) => (
          <NavLink key={to} to={`/student/${to}`} title={label}
            data-testid={`learn-now-rail-${to}`}
            className={({ isActive }) =>
              `w-9 h-9 rounded-lg flex items-center justify-center transition ${isActive ? 'text-white' : 'opacity-65 hover:opacity-100'}`
            }
            style={({ isActive }) => isActive ? { background: 'var(--st-primary)' } : {}}>
            <Icon className="w-4 h-4" />
          </NavLink>
        ))}
      </div>
    </div>
  );
}

function MainMenuDrawer({ open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);
  return (
    <div
      className={`fixed inset-0 z-[60] transition-opacity ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      data-testid="learn-now-mainmenu-drawer"
      aria-hidden={!open}>
      <div className="absolute inset-0 bg-black/45 backdrop-blur-[2px]" onClick={onClose} />
      <aside
        className={`absolute top-0 left-0 bottom-0 w-[300px] shadow-lg border-r transition-transform duration-300 ease-out flex flex-col ${open ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ background: 'var(--st-card)', borderColor: 'var(--st-border)', color: 'var(--st-ink)' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--st-border)' }}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center font-semibold text-sm" style={{ background: 'var(--st-primary)', color: 'var(--st-on-primary)' }}>D</div>
            <div className="leading-tight">
              <div className="font-semibold text-sm " style={{ fontFamily: 'var(--st-font-heading)' }}>MyDeskey</div>
              <div className="text-xs opacity-60 -mt-0.5">Main menu</div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-black/5 flex items-center justify-center" data-testid="learn-now-mainmenu-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-auto py-3 px-3">
          {/* Exact same sidebar as the dashboard shell — items, order and
              styling stay in lockstep because both render StudentSidebar. */}
          <StudentSidebar onNavigate={onClose} />
        </div>
      </aside>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Lessons sidebar (unchanged: chapter-grouped, collapsed by default)
// ═══════════════════════════════════════════════════════════════════════════
function LessonsSidebar({ open, onToggle, chapters, activeId, onPick, backHref }) {
  const activeChapterId = useMemo(() => {
    for (const c of chapters) if (c.lessons.some(l => l.id === activeId)) return c.id;
    return chapters[0]?.id;
  }, [chapters, activeId]);

  return (
    <aside
      className="border-r flex flex-col transition-[width] duration-300 ease-out overflow-hidden shrink-0"
      style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)', width: open ? 260 : 52 }}
      data-testid="learn-now-left-sidebar">
      <div className="flex items-center justify-between px-2 py-3 border-b" style={{ borderColor: 'var(--st-border)' }}>
        {open ? (
          <Link to={backHref} className="text-xs font-semibold inline-flex items-center gap-1.5 px-2 truncate" style={{ color: 'var(--st-mute)' }} data-testid="learn-now-class-link">
            <ChevronLeft className="w-3.5 h-3.5 shrink-0" /> Back to class
          </Link>
        ) : (
          <div className="w-9 h-9 rounded-lg flex items-center justify-center mx-auto" title="Lessons" style={{ background: 'var(--st-bg)' }}>
            <ListTree className="w-4 h-4" style={{ color: 'var(--st-primary)' }} />
          </div>
        )}
        <button onClick={onToggle}
          data-testid="learn-now-sidebar-toggle"
          title={open ? 'Collapse lessons' : 'Expand lessons'}
          aria-expanded={open}
          className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-black/5 shrink-0">
          {open ? <ChevronLeft className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
        </button>
      </div>
      {open
        ? <ExpandedChapterTree chapters={chapters} activeId={activeId} activeChapterId={activeChapterId} onPick={onPick} />
        : <CollapsedChapterRail chapters={chapters} activeChapterId={activeChapterId} onExpand={() => onToggle()} />}
    </aside>
  );
}

function CollapsedChapterRail({ chapters, activeChapterId, onExpand }) {
  if (!chapters.length) return null;
  return (
    <div className="flex-1 overflow-auto py-2 flex flex-col items-center gap-1.5" data-testid="learn-now-rail-chapters">
      {chapters.map(ch => {
        const a = ch.id === activeChapterId;
        return (
          <button key={ch.id} onClick={onExpand}
            data-testid={`learn-now-rail-chapter-${ch.id}`}
            title={`${ch.label} · ${ch.lessons.length} lesson${ch.lessons.length === 1 ? '' : 's'}`}
            className="w-9 h-9 rounded-lg flex flex-col items-center justify-center transition relative"
            style={{
              background: a ? 'var(--st-primary)' : 'var(--st-bg)',
              color: a ? '#fff' : 'inherit',
              border: a ? 'none' : '1px solid var(--st-border)',
            }}>
            <FolderOpen className="w-3.5 h-3.5" />
            <span className="text-xs font-semibold leading-none mt-0.5">{ch.lessons.length}</span>
          </button>
        );
      })}
    </div>
  );
}

function ExpandedChapterTree({ chapters, activeId, activeChapterId, onPick }) {
  const [openMap, setOpenMap] = useState(() => {
    const o = {}; chapters.forEach(c => { o[c.id] = c.id === activeChapterId; }); return o;
  });
  useEffect(() => { setOpenMap(prev => ({ ...prev, [activeChapterId]: true })); }, [activeChapterId]);

  return (
    <div className="flex-1 overflow-auto px-2 py-3" data-testid="learn-now-tree">
      <div className="text-xs font-semibold mb-2 px-2" style={{ color: 'var(--st-mute)' }}>
        Lessons · {chapters.reduce((n, c) => n + c.lessons.length, 0)}
      </div>
      <ul className="space-y-1.5">
        {chapters.map(ch => {
          const isOpen = !!openMap[ch.id];
          return (
            <li key={ch.id} className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--st-border)', background: 'var(--st-bg)' }}>
              <button onClick={() => setOpenMap(m => ({ ...m, [ch.id]: !m[ch.id] }))}
                data-testid={`learn-now-chapter-toggle-${ch.id}`}
                aria-expanded={isOpen}
                className="w-full flex items-center justify-between px-2.5 py-2 text-left">
                <span className="inline-flex items-center gap-1.5 min-w-0">
                  <FolderOpen className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--st-primary)' }} />
                  <span className="text-xs font-semibold truncate" style={{ fontFamily: 'var(--st-font-heading)' }}>{ch.label}</span>
                </span>
                <span className="inline-flex items-center gap-1 shrink-0">
                  <span className="text-xs font-medium tabular-nums px-1.5 rounded-full" style={TINT_PRIMARY}>{ch.lessons.length}</span>
                  {isOpen ? <ChevronLeft className="w-3 h-3 rotate-90 opacity-50" /> : <ChevronRightIcon className="w-3 h-3 opacity-50" />}
                </span>
              </button>
              {isOpen && (
                <ul className="border-t" style={{ borderColor: 'var(--st-border)' }}>
                  {ch.lessons.map(n => {
                    const a = n.id === activeId;
                    return (
                      <li key={n.id}>
                        <button onClick={() => onPick(n.id)}
                          data-testid={`learn-now-nav-${n.id}`}
                          className="w-full text-left px-3 py-2 transition flex items-start gap-2"
                          style={{ background: a ? 'var(--st-primary)' : 'transparent', color: a ? '#fff' : 'var(--st-ink)' }}>
                          <span className="w-1 h-1 rounded-full mt-2 shrink-0" style={{ background: a ? '#fff' : 'var(--st-primary)' }} />
                          <span className="min-w-0 flex-1">
                            <span className="block text-xs font-semibold leading-tight line-clamp-2" style={{ fontFamily: 'var(--st-font-heading)' }}>{n.title}</span>
                            <span className="block text-xs opacity-70 mt-0.5 inline-flex items-center gap-1">
                              <Calendar className="w-2.5 h-2.5" />
                              {fmtDate(n.published_at)} · {n.source === 'audio' ? 'Audio' : 'Framework'}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Right pane: tutor chat + notes — DRAG-RESIZABLE from its left edge
// ═══════════════════════════════════════════════════════════════════════════
function TutorPane({ lessonId, lessonTitle, sections = [], view, magicdeskGenId, open, setOpen, width, setWidth, tab, setTab, noteBlocks, setNoteBlocks, lessonCanvasRef, pendingWrongAnswerRef }) {
  const { me, theme } = useStudent();
  const themeAccent = theme?.primary || '#34A853';
  // The exam this student is preparing for — set once in Settings
  // (student_profile.target_exam), never re-picked here. null when unset.
  const targetExam = me?.student_profile?.target_exam || null;
  // Exam Mode is one fixed look per MODE -- light on every light theme, dark
  // on every dark theme (examPrepTheme.getExamMode*):
  // a per-theme gold under the theme's own bubble/tab colours and fonts
  // broke visually on every theme switch.
  const examDark = !!theme?.dark;
  const examColors = getExamModeColors(examDark);
  const examGoldGradient = getExamModeGoldGradient(examDark);
  const [sessionId, setSessionId] = useState(null);
  const [turns, setTurns] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);

  // "Discuss this wrong answer" entry point — open the panel and prefill
  // the composer once, on arrival. The ref itself is consumed (and its
  // wrong_answer_ref sent to the backend) by the very next send() call,
  // then cleared so later turns in this session are ordinary turns.
  useEffect(() => {
    if (!pendingWrongAnswerRef?.current) return;
    setOpen(true);
    setInput('I got this question wrong — can you help me understand where I went wrong?');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [chatModel, setChatModel] = useState(() => {
    try { return localStorage.getItem('desky_student_chat_model') || 'auto'; } catch { return 'auto'; }
  });
  const onChatModelChange = (k) => { setChatModel(k); try { localStorage.setItem('desky_student_chat_model', k); } catch { /* ignore storage errors */ } };
  // Exam Mode — on/off only (see ExamModeToggle.jsx). Which exam applies is
  // fixed by targetExam (the student's own Settings choice); this just
  // flips whether that persona is sent on the next turn. 'waveKey' bumps on
  // every OFF→ON flip so <ExamModeWave> remounts and replays its ripple.
  const [examModeOn, setExamModeOn] = useState(() => {
    try { return localStorage.getItem('desky_student_exam_mode_on') === '1'; } catch { return false; }
  });
  const [waveKey, setWaveKey] = useState(0);
  // Mounted only for the wave's own animation duration — the CSS animation
  // already ends invisible (opacity 0, fill-mode forwards), but unmounting
  // it too means it can never linger in the DOM as a visible blob if that
  // ever regresses (e.g. a style/build change dropping the animation).
  const [waveVisible, setWaveVisible] = useState(false);
  const waveTimeoutRef = useRef(null);
  // The wave's start point — the toggle button's own on-screen center,
  // measured against the panel it renders inside. Without this the ripple
  // has to guess a corner instead of visibly starting at the button.
  const [waveOrigin, setWaveOrigin] = useState(null);
  const asideRef = useRef(null);
  const examToggleRef = useRef(null);
  useEffect(() => () => clearTimeout(waveTimeoutRef.current), []);
  // One-time "what changed" card, shown the first time Exam Mode goes on.
  const [showExamIntro, setShowExamIntro] = useState(false);
  const onExamModeToggle = (next) => {
    setExamModeOn(next);
    setShowExamIntro(next && !hasSeenExamIntro());
    if (next) {
      const btnRect = examToggleRef.current?.getBoundingClientRect();
      const paneRect = asideRef.current?.getBoundingClientRect();
      if (btnRect && paneRect) {
        setWaveOrigin({
          x: btnRect.left + btnRect.width / 2 - paneRect.left,
          y: btnRect.top + btnRect.height / 2 - paneRect.top,
        });
      }
      setWaveKey(k => k + 1);
      setWaveVisible(true);
      clearTimeout(waveTimeoutRef.current);
      waveTimeoutRef.current = setTimeout(() => setWaveVisible(false), 1000);
    }
    try { localStorage.setItem('desky_student_exam_mode_on', next ? '1' : '0'); } catch { /* ignore storage errors */ }
  };
  // Notes — noteBlocks/setNoteBlocks are owned by the parent LearnNowPage
  // (a sticky can also live directly on the Lesson content, a sibling here).
  const scrollRef = useRef(null);
  const chatContentRef = useRef(null); // inner wrapper — observed so the view follows the streaming reveal
  const stickToBottomRef = useRef(true); // false once the student scrolls up to re-read
  const newestQuestionRef = useRef(null); // latest user bubble — anchored to the top on send
  const composerRef = useRef(null);   // textarea — used by InlineReferenceSuggest to insert at cursor
  const [resizing, setResizing] = useState(false);
  // HUB attachments the student picked for the next message. Carries over
  // across turns in the same lesson session until they clear it.
  const [hubAttachments, setHubAttachments] = useState([]);
  // FILE attachments — student-uploaded photos/PDFs (max 3 per message,
  // max 8MB each). These are SEPARATE from hubAttachments (which are
  // institution-published library docs). The student attaches their own
  // material here, e.g. a photo of a handwritten note or a textbook PDF.
  // Cleared after each send so the upload feels per-message, not sticky.
  const [fileAttachments, setFileAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  // Reference chips — URLs the student pastes OR previously-published lessons
  // they pick from their library. Appended as a compact "References:" footer
  // to the chat message on send so the LLM sees them as additional context.
  // Cleared after each send to keep refs per-message, not sticky.
  const [refChips, setRefChips] = useState([]);
  const [refLibrary, setRefLibrary] = useState([]);
  // '@'-mention SECTION chips — lesson sections the student attaches to
  // scope the tutor's grounding. Multi-select (one chip per section, deduped
  // by key). Sent as `focus_sections` on /send; cleared after each send so
  // scoping is per-question. Keys are "{index}:{title}" — the backend
  // validates both parts and falls back to the whole lesson on staleness.
  const [sectionChips, setSectionChips] = useState([]);
  const sectionOptions = useMemo(() => {
    const out = [];
    (sections || []).slice(0, 40).forEach((s, i) => {
      const title = s?.title || `Section ${i + 1}`;
      const key = `${i}:${s?.title || ''}`;
      out.push({ id: key, title });
      // Subsections listed for discoverability — picking one attaches the
      // PARENT section's key (the backend slices at section level).
      const subs = Array.isArray(s?.content) ? s.content : [];
      subs.slice(0, 20).forEach(sub => {
        if (sub && typeof sub === 'object' && sub.title) {
          out.push({ id: key, title: `${title} › ${sub.title}` });
        }
      });
    });
    return out;
  }, [sections]);
  // Per-image vision lookup — the student tapped "Explain this image"
  // on an inline image bubble. We track which turn is in-flight so the
  // bubble can show a spinner and disable the button.
  const [explainingTurnId, setExplainingTurnId] = useState(null);
  // Which assistant turn is currently being regenerated (disables the
  // button + shows a spinner state while the graph re-runs that turn).
  const [regeneratingId, setRegeneratingId] = useState(null);

  // ── Adaptive-framework state ─────────────────────────────────────────
  // Live preflight result for the text in the composer — shows the student
  // exactly what the next turn will cost and whether the model thinks they
  // want an image. Refreshed on a tiny debounce as they type.
  const [preflight, setPreflight] = useState(null);
  // After Send returns, if the backend offered an image (e.g. the student
  // said "show me a block diagram") this carries the offer until the
  // student confirms or skips. Rendered as an in-chat InlineImagePrompt
  // attached to the latest assistant turn.
  const [pendingImageOffer, setPendingImageOffer] = useState(null);
  // "/make image" slash command — typing / in the composer opens a small
  // command menu (same pattern as the Teacher Editor chat). Picking
  // "/make image" switches the composer into image-generation mode: the
  // InlineImageBuilder mounts in place of the text composer, grounded on
  // the tutor's most recent explanation.
  const [showCommandMenu, setShowCommandMenu] = useState(false);
  const [commandFilter, setCommandFilter] = useState('');
  const [commandIndex, setCommandIndex] = useState(0);
  const [composerImageMode, setComposerImageMode] = useState(false);
  // 402 from any tutor endpoint pops this — student must top up before
  // the paused action can resume. Carries needed/balance/reason so the
  // recharge sheet can explain WHY the buy is happening.
  const [recharge, setRecharge] = useState(null);
  // Two credit pools, synced from every backend response so the pills in
  // the chat header are always honest:
  //  • `balance`      — the TEXT-chat pool (institution credits; super only
  //                     for self-signup students with no institution).
  //  • `superBalance` — super credits, spent on image generation (and every
  //                     other paid generation feature).
  const [balance, setBalance] = useState(null);
  const [superBalance, setSuperBalance] = useState(null);
  // Snapshot of `balance` the first time it loads for this lesson — lets the
  // chat-header pill show real credits spent (opening balance - current) instead
  // of an approximate token count.
  const openingBalanceRef = useRef(null);

  // Chat is "context-aware": when the student is on the MagicDesk tab with
  // an active retune, the same chat window routes to the MagicDesk-grounded
  // endpoint (answers come from the rewritten version). Otherwise it talks
  // to the teacher's original lesson. No second chat box anywhere.
  const isMagicDesk = view === 'magicdesk' && !!magicdeskGenId;
  const contextLabel = isMagicDesk ? 'Tuned to your version' : 'Tuned to this lesson';

  useEffect(() => { setTurns([]); setSessionId(null); setHubAttachments([]); setFileAttachments([]); setRefChips([]); setSectionChips([]); setPendingImageOffer(null); }, [lessonId]);
  // Clear the running conversation when the chat context flips (lesson ↔
  // MagicDesk retune) so doubts don't bleed across grounded sources.
  useEffect(() => { setTurns([]); setSessionId(null); setPendingImageOffer(null); setSectionChips([]); }, [isMagicDesk, magicdeskGenId]);
  useEffect(() => {
    // The scroll container only exists in the DOM while the panel is open
    // (see `{open && tab === 'ask' && (...)}` below) — history loads before
    // the student ever opens it, so `scrollRef.current` is null when history
    // first populates and this effect no-ops. Opening the panel jumps to the
    // latest message (bottom). A fresh send does NOT jump here — send()
    // anchors the new question to the top instead (see scrollQuestionToTop).
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [open]);

  // Pin the student's newest question to the top of the viewport so the answer
  // streams into view *below* it — the question stays visible and reads in the
  // right order (question first, answer under it), instead of a long answer
  // scrolling the question off-screen. Called from send() after the user turn
  // is appended. `stickToBottom` goes false so the stream-follow observer
  // doesn't immediately yank the view back down to the answer's tail.
  const scrollQuestionToTop = () => {
    stickToBottomRef.current = false;
    // Double rAF: the first yields to React's commit (the new user bubble +
    // its ref land in the DOM), the second scrolls once it's laid out.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      newestQuestionRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }));
  };

  // The assistant reply types out char-by-char inside AssistantBubble via its
  // own local state — `turns` never changes during the reveal, so an effect on
  // `turns` can't follow it. Watch the chat content's size instead and keep the
  // viewport pinned to the bottom as the bubble grows — but ONLY while the
  // student is already parked at the bottom (short answers, or they scrolled
  // down to follow). A question anchored to the top leaves stick false, so the
  // answer fills the space below without hijacking the scroll position.
  useEffect(() => {
    const viewport = scrollRef.current;
    const content = chatContentRef.current;
    if (!open || !viewport || !content || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (stickToBottomRef.current) viewport.scrollTop = viewport.scrollHeight;
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, [open]);

  // Track whether the student is parked at the bottom. Within ~80px counts
  // as "following"; scrolling up further pauses auto-follow so re-reading
  // isn't yanked back down mid-stream.
  const handleChatScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  // ── Hydrate prior chat history for THIS lesson (across reloads) ──────
  // The new adaptive endpoint persists every turn keyed by lesson_id, so
  // when the student re-opens a lesson their conversation comes back. We
  // skip this on MagicDesk because that runs on its own grounded session.
  useEffect(() => {
    if (isMagicDesk) return;
    let mounted = true;
    // Default slim shape (no inline base64 image) — the chat bubble that
    // has `has_image: true` will lazy-load the bytes when it renders.
    api.get(`/student/tutor/lesson/${lessonId}/history?limit=50`)
      .then(d => {
        if (!mounted) return;
        const persisted = (d.turns || []).map(t => ({
          id: t.id,
          role: t.role,
          text: t.text || '',
          image: t.image || null,            // null in slim mode — TurnBubble lazy-loads via has_image
          has_image: !!t.has_image,
          intent: t.intent || null,
          cost: t.cost || 0,
          sections: focusTitles(t.focus_sections),
          attachments: (t.attachments || []).map(a => ({ ...a, fetchUrl: `/student/tutor/attachments/${a.id}` })),
          // Skip the typing reveal animation for restored history.
          restored: true,
        }));
        if (persisted.length) {
          // Lock to the most-recent session_id we find — keeps follow-ups
          // grouped under the same Gemini session.
          const lastSid = (d.turns || []).slice().reverse().find(t => t.session_id)?.session_id;
          if (lastSid) setSessionId(lastSid);
          setTurns(persisted);
        }
      })
      .catch(() => { /* fresh lesson, no history — ignore */ });
    return () => { mounted = false; };
  }, [lessonId, isMagicDesk]);

  // ── Hydrate the Reference picker's "Library" tab — pulls every lesson
  // the student is enrolled in. The overview endpoint already returns each
  // class's `next_up` lesson (which is what the student sees in the lesson
  // rail). We use that as the source of truth — the per-class `/lessons`
  // endpoint is stricter and often returns empty for students even when
  // they DO have visible lessons. Cheap; runs once per mount. ────────────
  useEffect(() => {
    let mounted = true;
    api.get('/student/mydesk/overview')
      .then(async (d) => {
        const classes = d?.classes || [];
        const seen = new Set();
        const lessons = [];
        // First pass — every class's `next_up` is guaranteed visible to
        // the student. We surface those right away.
        for (const c of classes) {
          const nx = c?.next_up;
          if (nx?.id && !seen.has(nx.id)) {
            seen.add(nx.id);
            lessons.push({
              id: nx.id,
              title: nx.title || 'Lesson',
              subject: c.class_name || c.subject_hint || '',
            });
          }
        }
        // Second pass — fold in every other published lesson across the
        // student's classes, in one call (best-effort, silent on error).
        try {
          const byClass = new Map(classes.map(c => [c?.class_id || c?.id, c]));
          const r = await api.get('/student/lesson-library');
          for (const l of (r?.lessons || [])) {
            if (l?.id && !seen.has(l.id)) {
              seen.add(l.id);
              const c = byClass.get(l.class_id) || {};
              lessons.push({
                id: l.id,
                title: l.title || 'Lesson',
                subject: c.class_name || c.subject_hint || '',
              });
            }
          }
        } catch { /* library fetch is best-effort — ignore */ }
        if (mounted) setRefLibrary(lessons.slice(0, 40));
      })
      .catch(() => { /* no library — ignore, the URL tab still works */ });
    return () => { mounted = false; };
  }, []);

  // ── Load both credit balances on mount so the pills in the chat header
  // always have a value to show — even before the student types anything.
  // Text chat bills institution credits (text_balance/text_pool); images
  // bill super credits (balance). ───────────────────────────────────────
  useEffect(() => {
    let mounted = true;
    openingBalanceRef.current = null;
    api.get('/student/tutor/credits/preflight?cost=1')
      .then(d => {
        if (!mounted) return;
        const b = typeof d.text_balance === 'number' ? d.text_balance
          : typeof d.balance === 'number' ? d.balance : null;
        if (b !== null) {
          setBalance(b);
          openingBalanceRef.current = b;
        }
        if (typeof d.balance === 'number') setSuperBalance(d.balance);
      })
      .catch(() => { /* non-fatal — pill stays hidden if this fails */ });
    return () => { mounted = false; };
  }, [lessonId]);

  // ── Live preflight: classify the composer text + report cost ─────────
  // We debounce by 250ms so we don't hit the backend on every keystroke.
  useEffect(() => {
    const trimmed = (input || '').trim();
    if (!trimmed || isMagicDesk) { setPreflight(null); return; }
    const handle = setTimeout(() => {
      api.post(`/student/tutor/lesson/${lessonId}/preflight`, { message: trimmed })
        .then(d => {
          setPreflight(d);
          // `balance` is the TEXT pool; `super_balance` is the image pool.
          if (typeof d.balance === 'number') setBalance(d.balance);
          if (typeof d.super_balance === 'number') setSuperBalance(d.super_balance);
        })
        .catch(() => { /* silent — preflight is best-effort */ });
    }, 250);
    return () => clearTimeout(handle);
  }, [input, lessonId, isMagicDesk]);

  // Global mouse/touch listeners while dragging the resize handle
  useEffect(() => {
    if (!resizing) return;
    const onMove = (ev) => {
      const cl = ev.touches ? ev.touches[0] : ev;
      setWidth(clampChatWidth(window.innerWidth - cl.clientX));
    };
    const onUp = () => setResizing(false);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [resizing, setWidth]);

  // Slash-command plumbing — mirrors the Teacher Editor chat interaction.
  const filteredCommands = useMemo(
    () => CHAT_COMMANDS.filter(c => c.cmd.slice(1).toLowerCase().startsWith(commandFilter.toLowerCase())),
    [commandFilter]
  );

  // Ground the composer image builder on the tutor's most recent text
  // explanation; fall back to the lesson title on an empty chat.
  const lastExplanation = useMemo(() => {
    const t = [...turns].reverse().find(x => x.role === 'assistant' && !x.image && (x.text || '').trim().length > 20);
    return t?.text || `Illustrate the key idea of "${lessonTitle}" for a student.`;
  }, [turns, lessonTitle]);

  const enterImageMode = () => {
    setShowCommandMenu(false);
    setInput('');
    setPreflight(null);
    setComposerImageMode(true);
  };

  const selectCommand = (c) => {
    if (!c) return;
    if (c.cmd === '/Generate image') { enterImageMode(); return; }
    setInput(`${c.cmd} `);
    setShowCommandMenu(false);
    composerRef.current?.focus();
  };

  const onComposerChange = (e) => {
    const val = e.target.value;
    setInput(val);
    if (isMagicDesk) { setShowCommandMenu(false); return; }
    // Command menu only while the composer holds nothing but a slash-prefix
    // (so "/" mid-sentence — fractions, URLs — never triggers it).
    const m = val.match(/^\/([\w ]*)$/);
    if (m) {
      setCommandFilter(m[1]);
      setShowCommandMenu(true);
      setCommandIndex(0);
    } else {
      setShowCommandMenu(false);
    }
  };

  const onComposerKeyDown = (e) => {
    if (showCommandMenu && filteredCommands.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setCommandIndex(i => Math.min(i + 1, filteredCommands.length - 1)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setCommandIndex(i => Math.max(i - 1, 0)); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); selectCommand(filteredCommands[commandIndex]); return; }
      if (e.key === 'Escape') { setShowCommandMenu(false); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  // Surface any Curiosity/streak badges the tutor turn just unlocked. Trophy
  // entries ({trophy_unlocked}) are toasted separately, same split as heartbeat.
  const celebrateEarned = (list) => {
    if (!Array.isArray(list) || !list.length) return;
    list.filter(x => !x.trophy_unlocked).forEach(showBadgeToast);
    list.filter(x => x.trophy_unlocked).forEach(x => showTrophyToast(x.trophy_unlocked));
  };

  // "Cleared my doubt" 👍 on an assistant reply → Resolution family
  // (total_doubts_cleared). One count per turn; the button locks after use.
  const [ratedTurns, setRatedTurns] = useState(() => new Set());
  const rateHelpful = async (turn) => {
    if (!turn?.id || ratedTurns.has(turn.id)) return;
    setRatedTurns(prev => new Set(prev).add(turn.id));
    try {
      const r = await api.post('/student/badges/event', {
        event_type: 'doubt_cleared', topic: lessonTitle, subject: undefined,
      });
      celebrateEarned(r?.newly_earned);
      toast.success('Marked as helpful');
    } catch {
      // Roll back the lock so the student can retry.
      setRatedTurns(prev => { const n = new Set(prev); n.delete(turn.id); return n; });
      toast.error("Couldn't record that — try again.");
    }
  };

  // The chat this reply belongs to. A student who switches lesson (or MagicDesk
  // version) while waiting must not get the old answer in the new chat.
  const chatKeyRef = useRef('');
  chatKeyRef.current = `${lessonId}|${isMagicDesk}|${magicdeskGenId}`;
  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    const sentKey = chatKeyRef.current;
    const switchedAway = () => chatKeyRef.current !== sentKey;
    // "/make image" typed in full — switch to image-generation mode instead
    // of sending it to the tutor as a message.
    if (!isMagicDesk && /^\/make\s*image\b/i.test(msg)) { enterImageMode(); return; }
    setSending(true);
    // Append any user-provided reference URLs / lesson IDs as a compact
    // "References:" footer so the LLM sees them as additional context.
    // Chips are cleared after the send so refs feel per-message, not sticky.
    const msgWithRefs = appendReferencesToMessage(msg, refChips);
    // Optimistically render the user bubble so the chat feels instant.
    const userTurnId = `u_${Date.now()}`;
    setTurns(t => [...t, {
      id: userTurnId, role: 'user', text: msgWithRefs, sections: sectionChips.map(c => c.label),
      attachments: fileAttachments.map(a => ({ id: a.id, kind: a.kind, filename: a.filename, src: a.local_url, fetchUrl: `/student/tutor/attachments/${a.id}` })),
    }]);
    scrollQuestionToTop();
    setInput('');
    setPreflight(null);
    const refsForThisTurn = refChips;
    setRefChips([]);
    const sectionChipsForThisTurn = sectionChips;
    const sectionsForThisTurn = sectionChips.map(c => c.id);
    setSectionChips([]);
    try {
      if (isMagicDesk) {
        // Route doubts to the retuned-lesson chat — answers are grounded
        // on the student's MagicDesk version, not the source lesson.
        const r = await api.post(`/student/generations/${magicdeskGenId}/magicdesk/chat`, { message: msgWithRefs });
        if (switchedAway()) return;
        setTurns(t => [...t, { id: `a_${Date.now()}`, role: 'assistant', text: r.reply || '(no reply)' }]);
        celebrateEarned(r.newly_earned_badges);
        // MagicDesk chat bills institution credits (real token cost), same
        // pool as the regular tutor text turns below.
        if (typeof r.credits_remaining === 'number') setBalance(r.credits_remaining);
        notifyCreditsChanged();
      } else {
        const wrongAnswerRef = pendingWrongAnswerRef?.current || undefined;
        if (pendingWrongAnswerRef) pendingWrongAnswerRef.current = null; // send once, not on later turns
        const r = await api.post(`/student/tutor/lesson/${lessonId}/send`, {
          session_id: sessionId,
          message: msgWithRefs,
          hub_doc_ids: hubAttachments.map(d => d.id),
          attachment_ids: fileAttachments.map(a => a.id),
          model_choice: chatModel,
          focus_sections: sectionsForThisTurn,
          wrong_answer_ref: wrongAnswerRef,
          exam_mode: (examModeOn && targetExam) ? targetExam : null,
        });
        if (switchedAway()) { notifyCreditsChanged(); return; }
        if (r.session_id) setSessionId(r.session_id);
        // Text turns bill the institution pool; the response says which pool
        // was actually charged (super fallback for no-institution students).
        if (typeof r.credits_remaining === 'number') {
          if (r.pool === 'super') setSuperBalance(r.credits_remaining);
          setBalance(r.credits_remaining);
        } else if (typeof r.super_credits_remaining === 'number') {
          setBalance(r.super_credits_remaining);
        }
        if (typeof r.image_offer?.balance === 'number') setSuperBalance(r.image_offer.balance);
        notifyCreditsChanged();
        // Clear staged file attachments — they've been folded into this
        // turn's context. The student can attach new ones for the next message.
        if (fileAttachments.length > 0) setFileAttachments([]);
        setTurns(t => [...t, {
          id: r.assistant_turn_id || `a_${Date.now()}`,
          role: 'assistant',
          text: r.reply || '(no reply)',
          intent: r.intent,
          cost: r.cost,
          model_used: r.model_used,
          model_reason: r.model_reason,
        }]);
        celebrateEarned(r.newly_earned_badges);
        // If the backend detected a visual intent ("show me a diagram of…"),
        // surface the confirmation modal so the student decides whether
        // to spend image-tier credits. Fall back to the cached preflight
        // result so the modal still pops even on a race where the /send
        // response lost the offer (cheap classifier disagrees with /send
        // version-skew, browser CORS retries, etc.).
        const offerFromSend = r.image_offer;
        const offerFromPreflight = preflight?.image_offer;
        if (offerFromSend) {
          setPendingImageOffer({ offer: offerFromSend, studentMessage: msg });
        } else if (offerFromPreflight) {
          setPendingImageOffer({ offer: offerFromPreflight, studentMessage: msg });
        }
      }
    } catch (e) {
      if (switchedAway()) return;
      // 402 = the canonical "you need to pay" — pause the task and pop
      // the recharge sheet with the exact shortfall.
      const status = e?.response?.status || e?.status;
      const detail = errorDetail(e); // studentApi: {status, body:{detail}}
      if (status === 402 && detail && typeof detail === 'object') {
        // Drop the optimistic user bubble — the message never got sent.
        setTurns(t => t.filter(x => x.id !== userTurnId));
        setInput(msg);
        // Restore the references so the student can resend after recharge.
        setRefChips(refsForThisTurn);
        setSectionChips(sectionChipsForThisTurn);
        if (detail.pool === 'institution') {
          // Institution pool is topped up by the institution, not by the
          // student — buying Super Credits would not unblock chat, so
          // don't pop the recharge sheet.
          setBalance(typeof detail.balance === 'number' ? detail.balance : 0);
          setTurns(t => [...t, {
            id: `e_${Date.now()}`, role: 'assistant',
            text: "You've run out of institution credits for chat. Please ask your institution to top up your monthly credits.",
          }]);
        } else {
          setRecharge({
            needed: detail.needed,
            balance: detail.balance,
            reason: 'Sending a message',
          });
        }
      } else {
        // Same text in the toast and the chat: an AI outage reads "The selected
        // AI assistant is currently unavailable…", a dropped connection says so.
        const text = notifyError(e, MESSAGES.aiUnavailable);
        setTurns(t => [...t, { id: `e_${Date.now()}`, role: 'assistant', text }]);
      }
    } finally { setSending(false); }
  };

  // ── File attachment upload ──────────────────────────────────────────────
  // Student picked one or more files from their device. Upload them serially
  // (so the chips appear one-by-one and one bad file doesn't poison the rest)
  // and append the returned ids to the staged-attachments list. Respect
  // the 3-files-per-message cap; surface friendly toasts for the rest.
  const MAX_FILES = 3;
  const MAX_BYTES = 8 * 1024 * 1024;
  const onPickFiles = async (ev) => {
    const picked = Array.from(ev.target.files || []);
    // Reset the input so the same file can be re-picked later
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!picked.length) return;
    const available = MAX_FILES - fileAttachments.length;
    if (available <= 0) {
      toast.error(`You can attach at most ${MAX_FILES} files per message`);
      return;
    }
    const toUpload = picked.slice(0, available);
    if (picked.length > available) {
      toast.message(`Attaching ${available} of ${picked.length} files (${MAX_FILES}-per-message cap).`);
    }
    setUploadingFile(true);
    for (const file of toUpload) {
      if (file.size > MAX_BYTES) {
        toast.error(`"${file.name}" is over 8 MB — skipped.`);
        continue;
      }
      const fd = new FormData();
      fd.append('file', file);
      try {
        const r = await api.upload(`/student/tutor/lesson/${lessonId}/attach`, fd);
        setFileAttachments(arr => [...arr, {
          id: r.id, kind: r.kind, filename: r.filename, mime: r.mime, size: r.size,
          // For images we stash a local objectURL so the chip can render a tiny
          // thumb instantly without round-tripping back through the auth'd
          // GET endpoint.
          local_url: r.kind === 'image' ? URL.createObjectURL(file) : null,
        }]);
      } catch (e) {
        notifyError(e, `Could not attach "${file.name}"`);
      }
    }
    setUploadingFile(false);
  };

  const removeFileAttachment = async (att) => {
    setFileAttachments(arr => arr.filter(x => x.id !== att.id));
    if (att.local_url) { try { URL.revokeObjectURL(att.local_url); } catch { /* ignore */ } }
    try { await api.delete(`/student/tutor/attachments/${att.id}`); } catch { /* fire-and-forget */ }
  };

  // ── Explain this image ──────────────────────────────────────────────────
  // Student tapped the "Explain this" button on an inline image bubble.
  // We call the multimodal vision endpoint (Gemini 2.5 Flash) which actually
  // LOOKS at the image and explains what's in it, grounded in the lesson.
  // On 402 we pop the recharge sheet; on success we append the explanation
  // as a new assistant turn so it sits right below the image.
  const explainImage = async (turn) => {
    if (!turn?.id || explainingTurnId) return;
    setExplainingTurnId(turn.id);
    try {
      const r = await api.post(`/student/tutor/lesson/${lessonId}/explain-image`, {
        image_turn_id: turn.id,
        session_id: sessionId,
      });
      // Explain-image is a TEXT turn — bills the institution pool.
      if (typeof r.credits_remaining === 'number') {
        if (r.pool === 'super') setSuperBalance(r.credits_remaining);
        setBalance(r.credits_remaining);
      } else if (typeof r.super_credits_remaining === 'number') {
        setBalance(r.super_credits_remaining);
      }
      notifyCreditsChanged();
      if (r.session_id) setSessionId(r.session_id);
      setTurns(arr => [...arr, {
        id: r.assistant_turn_id || `a_${Date.now()}`,
        role: 'assistant',
        text: r.reply || '(no reply)',
        cost: r.cost,
      }]);
    } catch (e) {
      const status = e?.status;
      const detail = e?.body?.detail;
      if (status === 402 && detail && typeof detail === 'object') {
        if (detail.pool === 'institution') {
          setBalance(typeof detail.balance === 'number' ? detail.balance : 0);
          toast.error('Out of institution credits — ask your institution to top up.');
        } else {
          setRecharge({
            needed: detail.needed,
            balance: detail.balance,
            reason: 'Explaining this image',
          });
        }
      } else {
        notifyError(e, "Couldn't explain this image. Please try again.");
      }
    } finally { setExplainingTurnId(null); }
  };

  // ── Image generation (confirmed by the student via ImageConfirmModal) ─
  const generateImage = async (prompt, kind, quality) => {
    if (!pendingImageOffer) return;
    try {
      const r = await api.post(`/student/tutor/lesson/${lessonId}/generate-image`, {
        session_id: sessionId,
        student_message: pendingImageOffer.studentMessage,
        prompt,
        kind,
        quality,
      });
      // Image generation bills SUPER credits.
      if (typeof r.super_credits_remaining === 'number') setSuperBalance(r.super_credits_remaining);
      notifyCreditsChanged();
      setTurns(t => [...t, {
        id: r.turn_id || `i_${Date.now()}`,
        role: 'assistant',
        text: '',
        image: r.image,
        cost: r.cost,
      }]);
      setPendingImageOffer(null);
    } catch (e) {
      const status = e?.response?.status || e?.status;
      const detail = errorDetail(e); // studentApi: {status, body:{detail}}
      if (status === 402 && detail && typeof detail === 'object') {
        setRecharge({
          needed: detail.needed,
          balance: detail.balance,
          reason: `Generating a ${kind?.replace('_', ' ') || 'image'}`,
        });
      } else {
        setTurns(t => [...t, { id: `e_${Date.now()}`, role: 'assistant', text: 'Image generation failed. Your credits were refunded.' }]);
        setPendingImageOffer(null);
      }
    }
  };

  // ── Clear chat for this lesson (soft delete on the server) ───────────
  const clearChat = async () => {
    if (!turns.length) return;
    if (!(await confirmDialog({ title: 'Clear chat', message: 'Clear the entire chat for this lesson? This cannot be undone.', danger: true, confirmLabel: 'Clear' }))) return;
    try {
      await api.delete(`/student/tutor/lesson/${lessonId}/clear`);
    } catch (e) {
      notifyError(e, "Couldn't clear the chat. Please try again.");
      return;
    }
    setTurns([]);
    setSessionId(null);
    setPendingImageOffer(null);
  };

  // ── Regenerate the last reply ─────────────────────────────────────────
  // No section-editing on this chat, so there's nothing to undo — this is
  // purely "try again". Backend re-runs from the LangGraph checkpoint right
  // before that turn, so it only ever targets the most recent assistant
  // reply (the button is only shown there — see TurnBubble usage below).
  const regenerate = async (turn) => {
    if (!turn?.id || regeneratingId || sending) return;
    setRegeneratingId(turn.id);
    try {
      const r = await api.post(`/student/tutor/lesson/${lessonId}/regenerate`, { session_id: sessionId });
      if (typeof r.credits_remaining === 'number') {
        if (r.pool === 'super') setSuperBalance(r.credits_remaining);
        setBalance(r.credits_remaining);
      }
      notifyCreditsChanged();
      if (r.session_id) setSessionId(r.session_id);
      setTurns(arr => {
        const idx = arr.findIndex(x => x.id === turn.id);
        const kept = idx === -1 ? arr : arr.slice(0, idx);
        return [...kept, {
          id: r.assistant_turn_id || `a_${Date.now()}`,
          role: 'assistant',
          text: r.reply || '(no reply)',
          model_used: r.model_used,
          model_reason: r.model_reason,
        }];
      });
    } catch (e) {
      const status = e?.status;
      const detail = e?.body?.detail;
      if (status === 402 && detail && typeof detail === 'object' && detail.pool === 'institution') {
        toast.error('Out of institution credits — ask your institution to top up.');
      } else {
        notifyError(e, "Couldn't regenerate that reply. Please try again.");
      }
    } finally { setRegeneratingId(null); }
  };

  const examModeActive = examModeOn && !!targetExam;

  return (
    <aside
      ref={asideRef}
      // Opts the panel out of StudentApp's hardcoded per-tier typography
      // rules (see its [data-exam-mode-panel] exclusions) while Exam Mode is on.
      data-exam-mode-panel={examModeActive ? '' : undefined}
      className="border-l flex flex-col overflow-hidden shrink-0 relative"
      style={{
        // Exam Mode overrides the theme's CSS variables for everything inside
        // the panel (bubbles, tabs, fonts, surfaces) -- same look on every theme.
        ...(examModeActive ? getExamModePanelVars(examDark, me?.student_profile?.accent_color || theme?.primary) : {}),
        // Explicit, not inherited: inherited `color` is computed at the shell
        // (light text on a dark theme), so without this, plain text in the
        // light Exam Mode panel ("My Notes", header icons) washed out.
        color: 'var(--st-ink)',
        borderColor: 'var(--st-border)', background: 'var(--st-card)', width: open ? width : 48,
        // The "spreads everywhere" part of Exam Mode: a quiet gold ring
        // around the whole panel, not just the button. Transitions in
        // rather than snapping, so it reads as the wave settling.
        boxShadow: examModeActive ? `inset 0 0 0 1.5px ${examColors.goldTint}, 0 0 28px -10px ${examColors.gold}` : 'none',
        transition: 'box-shadow 500ms ease',
      }}
      data-testid="learn-now-right-pane">

      {/* One-shot gold ripple, replayed via `waveKey` on every OFF→ON flip.
          Anchored bottom-right (where the toggle button sits in the
          composer) so the wave visibly originates from the button. */}
      {examModeActive && waveVisible && <ExamModeWave key={waveKey} origin={waveOrigin} dark={examDark} />}
      {examModeActive && waveVisible && <ExamModeSparkBurst key={`burst-${waveKey}`} origin={waveOrigin} dark={examDark} />}

      {/* Fixed-position, so it renders above the whole screen (not clipped
          by this aside's own overflow-hidden) regardless of tab/open state. */}
      <PinnedStickyBar blocks={noteBlocks} setBlocks={setNoteBlocks} />

      {open && (
        <button
          onMouseDown={(e) => { e.preventDefault(); setResizing(true); }}
          onTouchStart={(e) => { setResizing(true); }}
          data-testid="learn-now-chat-resizer"
          aria-label="Resize chat"
          title="Drag to resize"
          className="absolute top-0 left-0 bottom-0 w-1.5 cursor-col-resize z-20 group"
          style={{ background: resizing ? 'var(--st-primary)' : 'transparent' }}>
          <span className="absolute top-1/2 -translate-y-1/2 -left-0.5 w-2.5 h-10 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ background: 'var(--st-primary)' }} />
        </button>
      )}

      <div className="flex items-center justify-between px-3 py-3 border-b" style={{ borderColor: 'var(--st-border)' }}>
        {open ? (
          <div className="flex gap-1">
            {[['ask', 'Ask Tutor', MessagesSquare], ['notes', 'My Notes', NotebookPen]].map(([id, label, Icon]) => {
              const a = tab === id;
              return (
                <button key={id} onClick={() => setTab(id)}
                  data-testid={`tutor-tab-${id}`}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition"
                  style={{ background: a ? 'var(--st-primary)' : 'transparent', color: a ? 'var(--st-on-primary, #fff)' : 'inherit' }}>
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              );
            })}
          </div>
        ) : <span className="block" />}
        <div className="flex items-center gap-1">
          {/* Clear-chat button — only when there's something to clear */}
          {open && tab === 'ask' && turns.length > 0 && !isMagicDesk && (
            <button
              onClick={clearChat}
              data-testid="tutor-clear-chat"
              title="Clear this lesson's chat"
              className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-black/5">
              <Eraser className="w-3.5 h-3.5" />
            </button>
          )}
          {open && tab === 'ask' && (
            <TypingMuteToggle />
          )}
          {/* Only rendered while open (acts as Close) — when collapsed, the
              single centered button below is the sole reopen affordance so
              there's never two overlapping chat toggles on screen. */}
          {open && (
            <button onClick={() => setOpen(false)}
              data-testid="learn-now-tutor-toggle"
              className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-black/5">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {open && tab === 'ask' && (
        <>
          <div ref={scrollRef} onScroll={handleChatScroll} className="flex-1 overflow-auto px-3 py-3" data-testid="tutor-chat-scroll"
            style={{
              background: examModeActive
                ? `linear-gradient(180deg, ${examColors.goldTint}, transparent 30%, transparent 70%, ${examColors.goldTint})`
                : 'transparent',
              transition: 'background 500ms ease',
            }}>
            <div ref={chatContentRef} className="space-y-2">
            {turns.length === 0 && (
              <div className="rounded-xl p-3.5 text-xs" style={{ background: 'var(--st-bg)', color: 'var(--st-mute)' }}>
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="font-semibold" style={{ color: 'var(--st-ink)', fontFamily: 'var(--st-font-heading)' }}>
                    Deskey Tutor is ready.
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full flex-shrink-0"
                    style={{
                      background: isMagicDesk ? 'color-mix(in srgb, #8B5CF6 14%, transparent)' : 'color-mix(in srgb, var(--st-primary) 14%, transparent)',
                      color:      isMagicDesk ? '#8B5CF6' : 'var(--st-primary)',
                    }}
                    data-testid="tutor-context-badge">
                    {isMagicDesk ? <Sparkles className="w-3 h-3" /> : <BookOpen className="w-3 h-3" />}
                    {contextLabel}
                  </span>
                </div>
                {isMagicDesk
                  ? 'Ask doubts about your retuned lesson — answers come from your version.'
                  : "Ask anything about this lesson. I'll only answer from its material."}
              </div>
            )}
            {(() => {
              // Anchor ref goes on the newest user bubble so send() can pull it
              // to the top of the viewport (answer streams in below it).
              const lastUserIdx = turns.map(x => x.role).lastIndexOf('user');
              return turns.map((t, i) => (
                <div key={t.id || i} ref={i === lastUserIdx ? newestQuestionRef : null} style={{ scrollMarginTop: '8px' }}>
                  <TurnBubble
                    turn={t}
                    lessonId={lessonId}
                    onExplainImage={explainImage}
                    isExplaining={explainingTurnId === t.id}
                    // Regenerate only ever targets the LAST turn — the backend
                    // re-runs from that turn's pre-turn checkpoint, and only
                    // text replies can be regenerated (not an image bubble).
                    onRegenerate={
                      !isMagicDesk && i === turns.length - 1 && t.role === 'assistant' && !t.image && !t.pending
                        ? () => regenerate(t)
                        : null
                    }
                    isRegenerating={regeneratingId === t.id}
                    // "Cleared my doubt" 👍 on assistant replies (skip image bubbles).
                    onHelpful={t.role === 'assistant' && !t.image && !t.pending ? () => rateHelpful(t) : null}
                    helpfulDone={ratedTurns.has(t.id)}
                  />
                </div>
              ));
            })()}
            {/* Inline image-offer popup — rendered as the very last "turn"
                when the model detected a visual intent. Disappears once
                the student clicks Generate (image bubble lands above) or
                Skip. This is aesthetically smaller than a modal so the
                student stays in the conversation flow. */}
            {pendingImageOffer && !sending && (
              <InlineImagePrompt
                offer={pendingImageOffer.offer}
                onGenerate={generateImage}
                onSkip={() => setPendingImageOffer(null)}
              />
            )}
            {sending && <TurnBubble turn={{ role: 'assistant', text: '…', pending: true }} />}
            </div>
          </div>
          <div className="border-t px-3 pt-2 pb-2.5 flex flex-col gap-1.5" style={{ borderColor: 'var(--st-border)' }}>
            {/* Intent hint — surfaces what the next turn will cost and the
                detected shape, so the student isn't guessing what they're
                about to spend. Shrinks to a tiny pill at idle. */}
            {!isMagicDesk && preflight && input.trim() && (
              <IntentHint preflight={preflight} />
            )}
            {/* Stacked attachment chips — HUB, file uploads, and reference
                links all land in one rail above the composer so the student
                always sees what context the next turn will carry. */}
            {/* Attached photos / PDFs — thumbnails above the composer, like ChatGPT */}
            {(fileAttachments.length > 0 || uploadingFile) && (
              <div className="flex flex-wrap gap-2 mb-1.5" data-testid="tutor-file-tiles">
                <AttachmentStrip
                  items={fileAttachments.map(a => ({ id: a.id, kind: a.kind, filename: a.filename, src: a.local_url }))}
                  onRemove={(item) => removeFileAttachment(fileAttachments.find(a => a.id === item.id))}
                />
                {uploadingFile && <UploadingTile />}
              </div>
            )}
            {(hubAttachments.length > 0 || refChips.length > 0 || sectionChips.length > 0) && (
              <div className="flex flex-wrap gap-1.5" data-testid="tutor-attachment-rail">
                {hubAttachments.map(a => (
                  <span key={a.id}
                    data-testid={`tutor-hub-chip-${a.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold pl-1.5 pr-1 py-1 rounded-full border-2"
                    style={{ borderColor: 'var(--st-primary)', background: 'color-mix(in srgb, var(--st-primary) 8%, transparent)', color: 'var(--st-primary)' }}>
                    <FileTextIcon className="w-2.5 h-2.5" />
                    <span className="max-w-[140px] truncate">{a.title}</span>
                    <button onClick={() => setHubAttachments(arr => arr.filter(x => x.id !== a.id))}
                      data-testid={`tutor-hub-chip-remove-${a.id}`}
                      aria-label="Remove" className="ml-0.5 hover:bg-black/10 rounded-full w-3.5 h-3.5 inline-flex items-center justify-center">
                      <X className="w-2 h-2" />
                    </button>
                  </span>
                ))}
                {refChips.map(r => (
                  <span key={r.id}
                    data-testid={`tutor-ref-chip-${r.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold pl-1.5 pr-1 py-1 rounded-full border-2"
                    style={{ borderColor: '#0EA5E9', background: 'color-mix(in srgb, #0EA5E9 8%, transparent)', color: '#0369A1' }}
                    title={r.url || r.label}>
                    <span className="w-3 h-3 inline-flex items-center justify-center">
                      {r.kind === 'lesson' ? <BookOpen className="w-2.5 h-2.5" /> : <Link2 className="w-2.5 h-2.5" />}
                    </span>
                    <span className="max-w-[140px] truncate">{r.label}</span>
                    <button onClick={() => setRefChips(arr => arr.filter(x => x.id !== r.id))}
                      data-testid={`tutor-ref-chip-remove-${r.id}`}
                      aria-label="Remove reference" className="ml-0.5 hover:bg-black/10 rounded-full w-3.5 h-3.5 inline-flex items-center justify-center">
                      <X className="w-2 h-2" />
                    </button>
                  </span>
                ))}
                {sectionChips.map(s => (
                  <span key={s.id}
                    data-testid={`tutor-section-chip-${s.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold pl-1.5 pr-1 py-1 rounded-full border-2"
                    style={{ borderColor: 'var(--st-primary, #34A853)', background: 'color-mix(in srgb, var(--st-primary, #34A853) 8%, transparent)', color: 'var(--st-ink, #0f172a)' }}
                    title={`Grounding scoped to: ${s.label}`}>
                    <span className="w-3 h-3 inline-flex items-center justify-center">
                      <BookOpen className="w-2.5 h-2.5" />
                    </span>
                    <span className="max-w-[140px] truncate">{s.label}</span>
                    <button onClick={() => setSectionChips(arr => arr.filter(x => x.id !== s.id))}
                      data-testid={`tutor-section-chip-remove-${s.id}`}
                      aria-label="Remove section" className="ml-0.5 hover:bg-black/10 rounded-full w-3.5 h-3.5 inline-flex items-center justify-center">
                      <X className="w-2 h-2" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* "/make image" mode — the chat box swaps into the image-
                generation UI (same InlineImageBuilder as the old per-bubble
                flow), grounded on the tutor's latest explanation. Cancel or
                a successful generate returns the text composer. */}
            {composerImageMode && !isMagicDesk && (
              <InlineImageBuilder
                lessonId={lessonId}
                sessionId={sessionId}
                explanationText={lastExplanation}
                // Images bill SUPER credits — null (not loaded yet) lets the server decide.
                balance={superBalance}
                onCancel={() => setComposerImageMode(false)}
                onGenerated={({ image, turn_id, session_id, cost, new_balance }) => {
                  // The first image in a new chat starts the session (server-side).
                  if (session_id && !sessionId) setSessionId(session_id);
                  // Images bill SUPER credits.
                  if (typeof new_balance === 'number') setSuperBalance(new_balance);
                  notifyCreditsChanged();
                  setTurns(arr => [...arr, {
                    id: turn_id || `i_${Date.now()}`,
                    role: 'assistant',
                    text: '',
                    image,
                    cost,
                  }]);
                  setComposerImageMode(false);
                }}
                onInsufficient={({ needed, balance: bal, reason }) => {
                  setComposerImageMode(false);
                  setRecharge({ needed, balance: bal, reason });
                }}
              />
            )}
            {showExamIntro && examModeActive && !isMagicDesk && !composerImageMode && (
              <ExamModeIntro targetExam={targetExam} dark={examDark} onDismiss={() => setShowExamIntro(false)} />
            )}
            {/* Composer — single big container. The textarea spans the full
                width (no right-side column to steal space) and the action
                toolbar lives along the bottom inside the same rounded shell,
                Notion/Claude-style. */}
            {!composerImageMode && (
            <div
              className="rounded-2xl transition shadow-sm focus-within:shadow-lg relative"
              style={{
                // Flat, full-coverage gold — no fade to plain background,
                // the whole composer reads as gold while Exam Mode is on.
                background: examModeActive ? `${examColors.gold}40` : 'var(--st-bg)',
                border: examModeActive ? `1.5px solid ${examColors.gold}` : '1.5px solid var(--st-border)',
                transition: 'background 500ms ease, border-color 500ms ease',
              }}
              data-testid="tutor-composer-shell">
              {/* Slash-command menu — opens while the composer holds only a
                  "/" prefix. Same interaction as the Teacher Editor chat. */}
              {showCommandMenu && filteredCommands.length > 0 && (
                <div
                  className="absolute bottom-[calc(100%+6px)] left-2 w-[280px] rounded-xl bg-white border border-slate-200 p-1 z-50"
                  style={{ boxShadow: '0 14px 40px -10px rgba(15,23,42,0.18), 0 4px 12px -4px rgba(15,23,42,0.08)' }}
                  data-testid="tutor-command-menu">
                  {filteredCommands.map((c, i) => (
                    <button
                      key={c.cmd}
                      onMouseDown={(e) => { e.preventDefault(); selectCommand(c); }}
                      data-testid={`tutor-command-${c.cmd.slice(1).replace(/\s+/g, '-')}`}
                      className={`w-full text-left px-2.5 py-2 rounded-lg flex flex-col transition-colors ${i === commandIndex ? 'bg-slate-100' : 'hover:bg-slate-50'}`}>
                      <span className="text-xs font-semibold text-slate-800 inline-flex items-center gap-1.5">
                        <ImageIcon className="w-3 h-3" style={{ color: 'var(--st-primary)' }} />
                        {c.cmd}
                      </span>
                      <span className="text-xs text-slate-500">{c.desc}</span>
                    </button>
                  ))}
                </div>
              )}
              <textarea value={input}
                ref={composerRef}
                maxLength={3500} /* server caps at 4000; leaves room for added @-refs */
                onChange={onComposerChange}
                onKeyDown={onComposerKeyDown}
                placeholder={isMagicDesk
                  ? 'Ask a doubt about your retuned version…'
                  : `Ask about "${lessonTitle.slice(0, 40)}${lessonTitle.length > 40 ? '…' : ''}" — type @ to focus a section, / for commands`}
                data-testid="tutor-input"
                className="w-full bg-transparent px-4 pt-3 pb-1 text-sm leading-relaxed resize-none outline-none border-0"
                rows={5}
                style={{
                  color: 'var(--st-ink)',
                  fontFamily: 'var(--st-font-body, "Inter", system-ui, -apple-system, sans-serif)',
                  minHeight: '128px',
                  maxHeight: '320px',
                }} />
              {/* Inline @-mention autocomplete for the CURRENT lesson's
                  sections — bare '@' opens the full list; picking one
                  attaches a section chip that scopes the tutor's grounding
                  via `focus_sections`. Hidden in MagicDesk mode (different
                  grounding doc). */}
              <InlineReferenceSuggest
                textareaRef={composerRef}
                value={input}
                setValue={setInput}
                library={isMagicDesk ? [] : sectionOptions}
                minQueryLength={0}
                // Every section — a cap of 8 hid Revision Corner & co.
                // until the student typed their name. The list scrolls.
                maxItems={sectionOptions.length}
                headerLabel="Lesson sections"
                onPick={(item) => setSectionChips(arr => arr.some(x => x.id === item.id) ? arr : [...arr, { id: item.id, label: item.title }])}
                testIdPrefix="student-chat-section-suggest"
              />
              {/* Bottom action toolbar — inside the composer shell so the
                  whole thing feels like one element. Left: insertion tools
                  (file attach, HUB attach, reference link). Right: model
                  picker + send. flex-nowrap keeps the insertion buttons on
                  one row at the typical right-pane width (~380px). */}
              <div className="px-2 pb-2 pt-1 flex items-end justify-between gap-1.5">
                <div className="flex items-center gap-1 flex-nowrap min-w-0" data-testid="tutor-composer-actions-left">
                  {/* File attach — photos / PDFs (max 3 per message, 8 MB each).
                      Image text/structure is read by Gemini Vision on /send. */}
                  {!isMagicDesk && (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,application/pdf"
                        onChange={onPickFiles}
                        style={{ display: 'none' }}
                        data-testid="tutor-file-input"
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingFile || fileAttachments.length >= 3}
                        data-testid="tutor-file-attach"
                        title={fileAttachments.length >= 3
                          ? `Max ${3} files per message`
                          : 'Attach a photo or PDF (max 8 MB)'}
                        className="w-9 h-9 rounded-full flex items-center justify-center border-2 transition disabled:opacity-40 flex-shrink-0"
                        style={{
                          borderColor: fileAttachments.length > 0 ? '#0EA5E9' : 'var(--st-border)',
                          background: fileAttachments.length > 0 ? 'color-mix(in srgb, #0EA5E9 12%, transparent)' : 'transparent',
                          color: fileAttachments.length > 0 ? '#0369A1' : 'var(--st-mute)',
                        }}>
                        <Paperclip className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {/* Reference link — paste a URL or pick a lesson from the
                      library to attach as additional context for the LLM. */}
                  <ReferencePopover
                    value={refChips}
                    onChange={setRefChips}
                    library={refLibrary}
                    align="left"
                    anchor="top"
                    maxWidth={width - 60}
                    testIdPrefix="student-chat-ref"
                    accent={examModeActive ? examColors.text : themeAccent}
                    size="sm"
                  />
                </div>
                <div className="flex items-center gap-2 pb-0.5 flex-nowrap min-w-0" data-testid="tutor-composer-actions-right">
                  {!isMagicDesk && (
                    <span ref={examToggleRef} className="inline-flex flex-shrink-0">
                      <ExamModeToggle
                        targetExam={targetExam}
                        dark={examDark}
                        active={examModeOn}
                        onToggle={onExamModeToggle}
                        testIdPrefix="student-chat-exam-mode"
                      />
                    </span>
                  )}
                  {!isMagicDesk && (
                    <span className="inline-flex flex-shrink-0">
                      <ModelPicker value={chatModel} onChange={onChatModelChange} align="right" anchor="top" maxWidth={width - 130} testIdPrefix="student-chat-model" />
                    </span>
                  )}
                  <button onClick={send} disabled={!input.trim() || sending}
                    data-testid="tutor-send"
                    className="h-10 rounded-full flex items-center justify-center gap-1.5 text-white disabled:opacity-40 shadow-md hover:shadow-lg active:scale-95 transition px-4 font-semibold text-xs flex-shrink-0"
                    style={{
                      background: isMagicDesk ? '#8B5CF6' : (examModeActive ? examGoldGradient : 'var(--st-primary)'),
                      color: (examModeActive && !isMagicDesk) ? examColors.onGold : '#fff',
                    }}>
                    <Send className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Send</span>
                  </button>
                </div>
              </div>
            </div>
            )}
          </div>
        </>
      )}

      {open && tab === 'notes' && (
        <NotesPanel blocks={noteBlocks} setBlocks={setNoteBlocks} lessonCanvasRef={lessonCanvasRef} />
      )}

      {!open && (
        <button onClick={() => setOpen(true)} className="flex-1 flex flex-col items-center justify-center gap-1 text-xs font-semibold "
          style={{ color: 'var(--st-mute)' }} title="Open Deskey Tutor">
          <MessagesSquare className="w-4 h-4" />
        </button>
      )}

      {/* Recharge gate — opens whenever a tutor endpoint returns 402 or
          when the student taps the credits pill manually. */}
      {recharge && (
        <RechargeCreditsModal
          needed={recharge.needed}
          balance={recharge.balance}
          reason={recharge.reason}
          onClose={() => setRecharge(null)}
          onTopUpComplete={(newBal) => { setSuperBalance(newBal); setRecharge(null); notifyCreditsChanged(); }}
        />
      )}
    </aside>
  );
}

// IntentHint — tiny pill above the composer that shows the detected
// answer shape (length + format + visual flag) and what this turn will
// cost. Mirrors the Claude-style "you're about to spend X" affordance.
function IntentHint({ preflight }) {
  if (!preflight?.intent) return null;
  // Answer length is fixed server-side (not derived from the message), so we
  // no longer show a length pill — only the detected FORMAT and image flag.
  const { format, wants_image, image_kind } = preflight.intent;
  const fmtLabel =
    format === 'bullets' ? 'bullets' :
    format === 'steps'   ? 'steps'   :
    format === 'table'   ? 'table'   :
    format === 'code'    ? 'code'    : null;
  return (
    <div
      className="flex items-center gap-1.5 text-xs font-semibold opacity-80"
      data-testid="tutor-intent-hint"
      title="Auto-detected reply shape from your message">
      {fmtLabel && (
        <span className="px-1.5 py-0.5 rounded"
          style={{ background: 'var(--st-bg)', border: '1px solid var(--st-border)' }}>
          {fmtLabel}
        </span>
      )}
      {wants_image && (
        <span className="px-1.5 py-0.5 rounded inline-flex items-center gap-1"
          style={{ background: 'color-mix(in srgb, #8B5CF6 14%, transparent)', color: '#7C3AED', border: '1px solid #8B5CF6' }}>
          <ImageIcon className="w-2.5 h-2.5" />
          {image_kind?.replace('_', ' ') || 'image'}
        </span>
      )}
      <span className="ml-auto opacity-70">
        {preflight.text_cost} cr · {preflight.balance} left
      </span>
    </div>
  );
}

function TypingMuteToggle() {
  const [muted, setMutedLocal] = React.useState(isTypingMuted());
  return (
    <button onClick={() => { const v = !muted; setMutedLocal(v); setTypingMuted(v); }}
      data-testid="tutor-typing-mute"
      title={muted ? 'Typing sound is off' : 'Typing sound is on'}
      className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-black/5"
      style={{ color: muted ? 'var(--st-mute)' : 'var(--st-ink)' }}>
      {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
    </button>
  );
}

// '@'-mention focus keys are "<index>:<section title>" — the title is the label.
function focusTitles(keys) {
  const titles = (Array.isArray(keys) ? keys : [])
    .map(k => String(k).split(':').slice(1).join(':').trim())
    .filter(Boolean);
  return [...new Set(titles)];
}

function TurnBubble({ turn, onExplainImage, isExplaining, lessonId, onRegenerate, isRegenerating, onHelpful, helpfulDone }) {
  const mine = turn.role === 'user';
  // Lazy-load the image bytes the first time this turn renders if the slim
  // history payload only flagged `has_image: true`. Keeps the chat scroll
  // light when 100+ historical turns each carry a ~1 MB illustration.
  const [lazyImage, setLazyImage] = React.useState(null);
  React.useEffect(() => {
    if (!turn.has_image || turn.image || lazyImage || !turn.id || !lessonId) return;
    let mounted = true;
    api.get(`/student/tutor/lesson/${lessonId}/message/${turn.id}/image`)
      .then(d => { if (mounted && d?.image) setLazyImage(d.image); })
      .catch(() => { /* silent — broken bubble is fine */ });
    return () => { mounted = false; };
  }, [turn.id, turn.has_image, turn.image, lessonId, lazyImage]);
  const renderImage = turn.image || lazyImage;
  // Image turn — render an inline picture card instead of a text bubble.
  if (renderImage) {
    return (
      <div className="group relative">
        <ChatImageBubble
          image={renderImage}
          onExplain={onExplainImage && turn.id ? () => onExplainImage(turn) : null}
          isExplaining={isExplaining}
        />
      </div>
    );
  }
  if (!mine) {
    // Assistant turns get the rich renderer: asterisk-clean, math-aware,
    // ChatGPT-style line-by-line reveal with a soft typing tick. Restored
    // history skips the reveal animation by feeding the full text in.
    return (
      <div className="group relative flex flex-col items-start">
        <AssistantBubble fullText={turn.text} pending={turn.pending} skipAnimation={turn.restored} />
        {!turn.pending && (turn.model_used || onRegenerate || onHelpful) && (
          <div className="mt-1 ml-1 flex items-center gap-2">
            {turn.model_used && (
              <ModelBadge modelKey={turn.model_used} reason={turn.model_reason} testId={`tutor-model-badge-${turn.id || ''}`} />
            )}
            {onHelpful && (
              <button onClick={onHelpful} disabled={helpfulDone}
                data-testid="tutor-bubble-helpful"
                title={helpfulDone ? 'Thanks for the feedback' : 'This cleared my doubt'}
                className="inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded-full hover:bg-black/5 disabled:opacity-100 disabled:cursor-default"
                style={{ color: helpfulDone ? 'var(--st-primary)' : 'var(--st-mute)' }}>
                <ThumbsUp className="w-3 h-3" />
                {helpfulDone ? 'Helpful' : 'Cleared my doubt'}
              </button>
            )}
            {onRegenerate && (
              <button onClick={onRegenerate} disabled={isRegenerating}
                data-testid="tutor-bubble-regenerate"
                title="Regenerate this response"
                className="inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded-full hover:bg-black/5 disabled:opacity-50"
                style={{ color: 'var(--st-mute)' }}>
                {isRegenerating ? <InlineSparkle size={14} label="Regenerating" /> : <RotateCcw className="w-3 h-3" />}
                {isRegenerating ? 'Regenerating…' : 'Regenerate'}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="group relative flex flex-col items-end gap-1.5">
      {turn.attachments?.length > 0 && <AttachmentStrip items={turn.attachments} size="lg" align="end" />}
      <div className="max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-[1.55] whitespace-pre-wrap"
        style={{
          background: 'var(--st-primary)',
          // --st-on-primary is only set by dark Exam Mode (theme primaries
          // can be yellow/cyan/white there); everywhere else it's white.
          color: 'var(--st-on-primary, #fff)',
          opacity: turn.pending ? 0.6 : 1,
          fontFamily: 'var(--st-font-body, "Inter", system-ui, -apple-system, sans-serif)',
        }}
        data-testid="tutor-bubble-me">
        {/* Tagged sections show once, above the text — never repeated inside it. */}
        {turn.sections?.length > 0 && (
          <span className="block text-xs font-semibold mb-1" style={{ opacity: 0.75 }} data-testid="tutor-bubble-tags">
            {turn.sections.map(s => `@${s}`).join('  ')}
          </span>
        )}
        {stripSectionMentions(turn.text, turn.sections)}
      </div>
    </div>
  );
}

// ─── Notebook block helpers ────────────────────────────────────────────────
// Each lesson keeps a private stack of blocks. Two block types today:
//   • `text`    — a flowing paragraph the student types into.
//   • `sticky`  — a coloured callout box, like a Post-it pinned in the
//                 middle of the notebook. The student can have as many as
//                 they want, in any of 5 colours, anywhere in the stack.
// Storage is per-lesson localStorage; on first load we migrate the previous
// plain-string format from v1 into a single text block so no notes are
// lost.
const NOTE_COLORS = [
  { id: 'yellow',   bg: '#FEF3C7', border: '#F59E0B', ink: '#78350F' },
  { id: 'pink',     bg: '#FCE7F3', border: '#EC4899', ink: '#831843' },
  { id: 'blue',     bg: '#DBEAFE', border: '#3B82F6', ink: '#1E3A8A' },
  { id: 'green',    bg: '#DCFCE7', border: '#22C55E', ink: '#14532D' },
  { id: 'lavender', bg: '#EDE9FE', border: '#8B5CF6', ink: '#4C1D95' },
];

// Fixed footprint for a sticky placed on the lesson content — no resize
// handles, just move + rotate (see CanvasSticky).
const CANVAS_STICKY_SIZE = STICKY_SIZE;

function loadNoteBlocks(lessonId) {
  try {
    const v2 = localStorage.getItem(`mydesk_notes_v2_${lessonId}`);
    if (v2) {
      const parsed = JSON.parse(v2);
      if (Array.isArray(parsed) && parsed.length) return parsed;
    }
    // Migrate any pre-existing plain-string note into a single text block.
    const v1 = localStorage.getItem(`mydesk_notes_${lessonId}`);
    if (v1 && v1.trim()) {
      return [{ id: `n_${Date.now()}`, type: 'text', body: v1 }];
    }
  } catch { /* fall through to empty */ }
  return [{ id: `n_${Date.now()}`, type: 'text', body: '' }];
}

function NotesPanel({ blocks, setBlocks, lessonCanvasRef }) {
  const addBlock = (type) => {
    const color = type === 'sticky' ? NOTE_COLORS[blocks.filter(b => b.type === 'sticky').length % NOTE_COLORS.length].id : undefined;
    setBlocks(b => [...b, { id: `n_${Date.now()}_${Math.random().toString(36).slice(2,7)}`, type, color, body: '', pinned: false, canvasPos: null }]);
  };
  const updateBlock = (id, patch) => setBlocks(b => b.map(x => x.id === id ? { ...x, ...patch } : x));
  const removeBlock = (id) => setBlocks(b => b.length <= 1 ? b : b.filter(x => x.id !== id));
  // Pinned stickies live in the floating top bar (see PinnedStickyBar) and
  // on-canvas ones live directly on the Lesson content (see CanvasSticky) —
  // keep both out of the inline stack so they don't render twice.
  const visible = blocks.filter(b => !(b.type === 'sticky' && (b.pinned || b.canvasPos)));

  return (
    <div className="flex-1 overflow-auto flex flex-col" data-testid="tutor-notes-panel">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: 'var(--st-border)' }}>
        <div className="text-xs font-semibold" style={{ color: 'var(--st-mute)' }}>Your private notes</div>
        <div className="flex items-center gap-1">
          <button onClick={() => addBlock('text')}
            data-testid="notes-add-text"
            title="Add paragraph"
            className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full hover:bg-black/5 transition-colors"
            style={{ color: 'var(--st-ink)', border: '1px solid var(--st-border)' }}>
            <Type className="w-3 h-3" /> Text
          </button>
          <button onClick={() => addBlock('sticky')}
            data-testid="notes-add-sticky"
            title="Add sticky"
            className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full text-white"
            style={{ background: '#F59E0B' }}>
            <Plus className="w-3 h-3" /> Sticky
          </button>
        </div>
      </div>

      {/* Block stack */}
      <div className="flex-1 overflow-auto p-3 space-y-2.5">
        {visible.length === 0 && (
          <div className="text-xs text-center py-6" style={{ color: 'var(--st-mute)' }}>
            All your stickies are pinned to the top of the screen.
          </div>
        )}
        {visible.map((blk) => (
          blk.type === 'sticky'
            ? <StickyBlock key={blk.id} block={blk} onChange={p => updateBlock(blk.id, p)} onRemove={() => removeBlock(blk.id)} canRemove={blocks.length > 1} lessonCanvasRef={lessonCanvasRef} />
            : <TextBlock   key={blk.id} block={blk} onChange={p => updateBlock(blk.id, p)} onRemove={() => removeBlock(blk.id)} canRemove={blocks.length > 1} />
        ))}
      </div>
    </div>
  );
}

// Floating strip fixed under the app header — sticky notes dragged to the
// top of the screen (see StickyBlock's grip handle) "stick" here instead of
// living inline in the Notes tab, so a reminder stays visible while the
// student scrolls the lesson or switches tabs. Each chip can be minimized
// (collapses to just its color dot) or removed (unpins it — the note's
// text is kept, it just returns to the inline Notes stack).
function PinnedStickyBar({ blocks, setBlocks }) {
  const [minimized, setMinimized] = useState({}); // { [blockId]: bool }
  const pinned = blocks.filter(b => b.type === 'sticky' && b.pinned);
  if (!pinned.length) return null;
  const unpin = (id) => setBlocks(b => b.map(x => x.id === id ? { ...x, pinned: false } : x));
  return (
    <div className="fixed top-16 left-2 right-2 sm:left-4 sm:right-4 z-40 flex flex-wrap gap-2 pointer-events-none" data-testid="pinned-sticky-bar">
      {pinned.map(blk => {
        const palette = NOTE_COLORS.find(c => c.id === blk.color) || NOTE_COLORS[0];
        const isMin = !!minimized[blk.id];
        return (
          <div key={blk.id}
            data-testid={`pinned-sticky-${blk.id}`}
            className="pointer-events-auto rounded-xl shadow-lg flex items-start gap-1.5 transition"
            style={{
              background: palette.bg, border: `1px solid ${palette.border}`, color: palette.ink,
              padding: isMin ? '6px' : '8px 10px',
              maxWidth: isMin ? 40 : 240,
            }}>
            <StickyNote className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: palette.border }} />
            {!isMin && (
              <p className="flex-1 min-w-0 text-xs leading-snug line-clamp-3" style={{ fontFamily: '"Caveat", "Patrick Hand", cursive' }}>
                {blk.body?.trim() || 'Empty sticky'}
              </p>
            )}
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <button onClick={() => setMinimized(m => ({ ...m, [blk.id]: !m[blk.id] }))}
                data-testid={`pinned-sticky-min-${blk.id}`}
                title={isMin ? 'Expand' : 'Minimize'}
                className="w-5 h-5 rounded-full flex items-center justify-center hover:brightness-95"
                style={{ background: 'rgba(255,255,255,0.55)', border: `1px solid ${palette.border}55` }}>
                <Minus className="w-2.5 h-2.5" />
              </button>
              {!isMin && (
                <button onClick={() => unpin(blk.id)}
                  data-testid={`pinned-sticky-remove-${blk.id}`}
                  title="Remove from top (keeps the note in your notes list)"
                  className="w-5 h-5 rounded-full flex items-center justify-center hover:brightness-95"
                  style={{ background: 'rgba(255,255,255,0.55)', border: `1px solid ${palette.border}55` }}>
                  <X className="w-2.5 h-2.5" />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TextBlock({ block, onChange, onRemove, canRemove }) {
  return (
    <div className="group relative rounded-xl" data-testid={`notes-block-text-${block.id}`}>
      <textarea value={block.body}
        onChange={e => onChange({ body: e.target.value })}
        placeholder="Jot what matters. Only you see this."
        className="w-full rounded-xl px-3 py-2 text-xs outline-none resize-none min-h-[96px]"
        style={{ background: 'var(--st-bg)', color: 'var(--st-ink)', border: '1px solid var(--st-border)' }} />
      {canRemove && (
        <button onClick={onRemove}
          data-testid={`notes-block-remove-${block.id}`}
          title="Delete block"
          className="opacity-0 group-hover:opacity-100 transition-opacity absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center"
          style={{ background: 'var(--st-card)', color: 'var(--st-mute)', border: '1px solid var(--st-border)' }}>
          <Trash2 className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function StickyBlock({ block, onChange, onRemove, canRemove, lessonCanvasRef }) {
  const palette = NOTE_COLORS.find(c => c.id === block.color) || NOTE_COLORS[0];
  // Drag-to-pin: grabbing the handle and releasing near the top of the
  // screen "sticks" this note to the floating PinnedStickyBar. Releasing it
  // over the Lesson content instead places it there (see CanvasSticky) —
  // only reachable when the Lesson tab is open (lessonCanvasRef.current is
  // null while any other tab is showing, since that wrapper isn't mounted).
  const [dragging, setDragging] = useState(false);
  const [dragPos, setDragPos] = useState(null);
  const PIN_ZONE_HEIGHT = 140;
  const overPinZone = !!(dragPos && dragPos.y < PIN_ZONE_HEIGHT);
  const lessonRect = (dragging && lessonCanvasRef?.current) ? lessonCanvasRef.current.getBoundingClientRect() : null;
  const overLessonCanvas = !!(dragPos && lessonRect && !overPinZone
    && dragPos.x >= lessonRect.left && dragPos.x <= lessonRect.right
    && dragPos.y >= lessonRect.top && dragPos.y <= lessonRect.bottom);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (ev) => {
      if (ev.cancelable) ev.preventDefault();
      const cl = ev.touches ? ev.touches[0] : ev;
      setDragPos({ x: cl.clientX, y: cl.clientY });
    };
    const onUp = () => {
      setDragPos(pos => {
        if (pos && pos.y < PIN_ZONE_HEIGHT) {
          onChange({ pinned: true });
        } else if (pos && lessonCanvasRef?.current) {
          const rect = lessonCanvasRef.current.getBoundingClientRect();
          if (pos.x >= rect.left && pos.x <= rect.right && pos.y >= rect.top && pos.y <= rect.bottom) {
            const canvasPos = toStored(
              { x: pos.x - rect.left - CANVAS_STICKY_SIZE / 2, y: pos.y - rect.top - CANVAS_STICKY_SIZE / 2 },
              rect, block.rotation || 0);
            onChange({ canvasPos, pinned: false });
          }
        }
        return null;
      });
      setDragging(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging]);

  const startDrag = (ev) => {
    ev.preventDefault();
    const cl = ev.touches ? ev.touches[0] : ev;
    setDragPos({ x: cl.clientX, y: cl.clientY });
    setDragging(true);
  };

  return (
    <>
    <div className="group relative rounded-xl p-3"
      data-testid={`notes-block-sticky-${block.id}`}
      style={{
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        boxShadow: '0 8px 20px -8px rgba(0,0,0,0.15), 0 1px 0 rgba(255,255,255,0.6) inset',
        transform: 'rotate(-0.4deg)',   // tiny tilt for the pinned-note feel
        opacity: dragging ? 0.35 : 1,
      }}>
      {/* Color picker dots */}
      <div className="flex items-center gap-1.5 mb-1.5">
        <button
          onMouseDown={startDrag}
          onTouchStart={startDrag}
          title="Drag to the top of the screen to pin it there"
          data-testid={`notes-block-drag-${block.id}`}
          className="cursor-grab active:cursor-grabbing -ml-1 p-0.5 rounded hover:bg-black/5 touch-none">
          <GripVertical className="w-3.5 h-3.5" style={{ color: palette.border }} />
        </button>
        <StickyNote className="w-3.5 h-3.5" style={{ color: palette.border }} />
        <div className="flex items-center gap-1">
          {NOTE_COLORS.map(c => (
            <button key={c.id}
              data-testid={`notes-block-color-${block.id}-${c.id}`}
              onClick={() => onChange({ color: c.id })}
              title={c.id}
              className="w-3 h-3 rounded-full transition-transform "
              style={{
                background: c.bg,
                border: `1.5px solid ${c.border}`,
                boxShadow: c.id === block.color ? `0 0 0 2px ${c.border}33` : 'none',
              }} />
          ))}
        </div>
        {canRemove && (
          <button onClick={onRemove}
            data-testid={`notes-block-remove-${block.id}`}
            title="Delete sticky"
            className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity w-6 h-6 rounded-full flex items-center justify-center"
            style={{ color: palette.ink, background: 'rgba(255,255,255,0.55)', border: `1px solid ${palette.border}55` }}>
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
      <textarea value={block.body}
        onChange={e => onChange({ body: e.target.value })}
        placeholder="Pin a quick thought — a question, a key term, a reminder…"
        className="w-full bg-transparent outline-none resize-none text-xs leading-snug min-h-[72px]"
        style={{ color: palette.ink, fontFamily: '"Caveat", "Patrick Hand", cursive' }} />
    </div>

    {/* Drag ghost — follows the pointer while dragging */}
    {dragging && dragPos && (
      <div className="fixed z-[70] pointer-events-none rounded-xl px-3 py-2 shadow-lg"
        style={{
          left: dragPos.x, top: dragPos.y, transform: 'translate(-50%, -50%) rotate(-3deg)',
          background: palette.bg, border: `2px solid ${palette.border}`, color: palette.ink,
          maxWidth: 200,
        }}>
        <p className="text-xs leading-snug line-clamp-3" style={{ fontFamily: '"Caveat", "Patrick Hand", cursive' }}>
          {block.body?.trim() || 'Empty sticky'}
        </p>
      </div>
    )}
    {/* Drop-zone hint — lights up the top strip of the screen while dragging */}
    {dragging && (
      <div className="fixed top-0 left-0 right-0 z-[65] flex items-center justify-center pointer-events-none transition-colors"
        style={{
          height: PIN_ZONE_HEIGHT,
          background: overPinZone ? 'color-mix(in srgb, var(--st-primary) 16%, transparent)' : 'transparent',
          borderBottom: overPinZone ? '2px dashed var(--st-primary)' : '2px dashed transparent',
        }}>
        {overPinZone && (
          <span className="text-xs font-semibold px-3 py-1.5 rounded-full" style={{ background: 'var(--st-primary)', color: 'var(--st-on-primary)' }}>
            Release to stick to the top
          </span>
        )}
      </div>
    )}
    {/* Drop-zone hint — highlights the Lesson content pane while dragging
        over it (only present when the Lesson tab is open). */}
    {dragging && lessonRect && (
      <div className="fixed z-[65] flex items-center justify-center pointer-events-none transition-colors rounded-xl"
        style={{
          left: lessonRect.left, top: lessonRect.top, width: lessonRect.width, height: lessonRect.height,
          background: overLessonCanvas ? 'color-mix(in srgb, var(--st-primary) 10%, transparent)' : 'transparent',
          border: overLessonCanvas ? '2px dashed var(--st-primary)' : '2px dashed transparent',
        }}>
        {overLessonCanvas && (
          <span className="text-xs font-semibold px-3 py-1.5 rounded-full" style={{ background: 'var(--st-primary)', color: 'var(--st-on-primary)' }}>
            Release to place on the lesson
          </span>
        )}
      </div>
    )}
    </>
  );
}

// Stickies dropped onto the Lesson content (see StickyBlock's grip handle).
// Rendered as siblings inside the same `relative` wrapper as LessonView, so
// each one's absolute {x,y} is just a fixed offset from that wrapper's
// top-left — no scroll-position bookkeeping needed, it moves with the page
// like any other content.
function CanvasStickyLayer({ blocks, setBlocks, lessonCanvasRef }) {
  const onCanvas = blocks.filter(b => b.type === 'sticky' && b.canvasPos);
  // The lesson area resizes (lessons sidebar, chat pane, zoom, window) —
  // re-place notes from their saved relative position whenever it does.
  const [area, setArea] = useState(null);
  useEffect(() => {
    const el = lessonCanvasRef.current;
    if (!el) return undefined;
    const measure = () => setArea({ width: el.clientWidth, height: el.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [lessonCanvasRef]);
  if (!area) return null;
  const updateBlock = (id, patch) => setBlocks(b => b.map(x => x.id === id ? { ...x, ...patch } : x));
  const removeBlock = (id) => setBlocks(b => b.length <= 1 ? b : b.filter(x => x.id !== id));
  const returnToNotes = (id) => setBlocks(b => b.map(x => x.id === id ? { ...x, canvasPos: null, pinned: false } : x));
  return (
    <>
      {onCanvas.map(blk => (
        <CanvasSticky key={blk.id} block={blk}
          onChange={p => updateBlock(blk.id, p)}
          onRemove={() => removeBlock(blk.id)}
          onReturnToNotes={() => returnToNotes(blk.id)}
          canRemove={blocks.length > 1}
          lessonCanvasRef={lessonCanvasRef}
          area={area} />
      ))}
    </>
  );
}

function CanvasSticky({ block, onChange, onRemove, onReturnToNotes, canRemove, lessonCanvasRef, area }) {
  const palette = NOTE_COLORS.find(c => c.id === block.color) || NOTE_COLORS[0];
  const boxRef = useRef(null);
  const grabOffset = useRef({ dx: 0, dy: 0 });
  const livePosRef = useRef(null);
  const liveRotationRef = useRef(null);
  const [moving, setMoving] = useState(false);
  const [livePos, setLivePos] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [liveRotation, setLiveRotation] = useState(null);

  const rotation = liveRotation !== null ? liveRotation : (block.rotation || 0);
  // Saved position is relative (see stickyPlacement.js); turned into pixels
  // for the area's current size, always fully inside it incl. rotated corners.
  const pos = livePos || toPixels(block.canvasPos, area, rotation);

  const startMove = (ev) => {
    ev.preventDefault();
    const cl = ev.touches ? ev.touches[0] : ev;
    const rect = lessonCanvasRef.current.getBoundingClientRect();
    grabOffset.current = {
      dx: cl.clientX - rect.left - pos.x,
      dy: cl.clientY - rect.top - pos.y,
    };
    setMoving(true);
  };

  useEffect(() => {
    if (!moving) return;
    const rot = block.rotation || 0;
    const onMove = (ev) => {
      if (ev.cancelable) ev.preventDefault();
      const cl = ev.touches ? ev.touches[0] : ev;
      const rect = lessonCanvasRef.current.getBoundingClientRect();
      const raw = { x: cl.clientX - rect.left - grabOffset.current.dx, y: cl.clientY - rect.top - grabOffset.current.dy };
      const p = toPixels(toStored(raw, rect, rot), rect, rot);
      livePosRef.current = p;
      setLivePos(p);
    };
    const onUp = () => {
      setMoving(false);
      if (livePosRef.current && lessonCanvasRef.current) {
        onChange({ canvasPos: toStored(livePosRef.current, lessonCanvasRef.current.getBoundingClientRect(), rot) });
      }
      livePosRef.current = null;
      setLivePos(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moving]);

  const startRotate = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    setRotating(true);
  };

  useEffect(() => {
    if (!rotating) return;
    const onMove = (ev) => {
      if (ev.cancelable) ev.preventDefault();
      const cl = ev.touches ? ev.touches[0] : ev;
      const box = boxRef.current.getBoundingClientRect();
      const cx = box.left + box.width / 2;
      const cy = box.top + box.height / 2;
      // Handle sits at the top-right corner (45deg from centre) — offset so
      // that position reads as 0deg rotation.
      const deg = (Math.atan2(cl.clientY - cy, cl.clientX - cx) * 180 / Math.PI) + 45;
      liveRotationRef.current = deg;
      setLiveRotation(deg);
    };
    const onUp = () => {
      setRotating(false);
      if (liveRotationRef.current !== null) {
        // A rotated note reaches further out — re-save its position nudged
        // inside so the corners never hang past the lesson's edge.
        const deg = liveRotationRef.current;
        const box = { width: area.width, height: area.height };
        onChange({ rotation: deg, canvasPos: toStored(toPixels(block.canvasPos, box, deg), box, deg) });
      }
      liveRotationRef.current = null;
      setLiveRotation(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rotating]);

  return (
    <div ref={boxRef}
      className="group absolute rounded-xl p-3 select-none"
      data-testid={`canvas-sticky-${block.id}`}
      style={{
        left: pos.x, top: pos.y, width: CANVAS_STICKY_SIZE, height: CANVAS_STICKY_SIZE,
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        boxShadow: '0 10px 24px -10px rgba(0,0,0,0.28), 0 1px 0 rgba(255,255,255,0.6) inset',
        transform: `rotate(${rotation}deg)`,
        zIndex: (moving || rotating) ? 30 : 10,
        cursor: moving ? 'grabbing' : 'default',
      }}>
      <div className="flex items-center gap-1 mb-1.5">
        <button
          onMouseDown={startMove}
          onTouchStart={startMove}
          title="Drag to move"
          data-testid={`canvas-sticky-move-${block.id}`}
          className="cursor-grab active:cursor-grabbing -ml-1 p-0.5 rounded hover:bg-black/5 touch-none">
          <GripVertical className="w-3.5 h-3.5" style={{ color: palette.border }} />
        </button>
        <StickyNote className="w-3.5 h-3.5" style={{ color: palette.border }} />
        <div className="ml-auto flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={onReturnToNotes}
            title="Return to notes list"
            data-testid={`canvas-sticky-return-${block.id}`}
            className="w-5 h-5 rounded-full flex items-center justify-center hover:brightness-95"
            style={{ color: palette.ink, background: 'rgba(255,255,255,0.55)', border: `1px solid ${palette.border}55` }}>
            <NotebookPen className="w-2.5 h-2.5" />
          </button>
          {canRemove && (
            <button onClick={onRemove}
              title="Delete sticky"
              data-testid={`canvas-sticky-remove-${block.id}`}
              className="w-5 h-5 rounded-full flex items-center justify-center hover:brightness-95"
              style={{ color: palette.ink, background: 'rgba(255,255,255,0.55)', border: `1px solid ${palette.border}55` }}>
              <Trash2 className="w-2.5 h-2.5" />
            </button>
          )}
        </div>
      </div>
      <textarea value={block.body}
        onChange={e => onChange({ body: e.target.value })}
        onMouseDown={e => e.stopPropagation()}
        placeholder="Pin a quick thought…"
        className="w-full bg-transparent outline-none resize-none text-xs leading-snug"
        style={{ color: palette.ink, fontFamily: '"Caveat", "Patrick Hand", cursive', height: CANVAS_STICKY_SIZE - 56 }} />
      {/* Rotate handle — drag in a circle around the note's centre to tilt it */}
      <div onMouseDown={startRotate}
        onTouchStart={startRotate}
        title="Drag to rotate"
        data-testid={`canvas-sticky-rotate-${block.id}`}
        className="absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center cursor-alias touch-none opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ background: palette.border, color: '#fff' }}>
        <RotateCw className="w-3 h-3" />
      </div>
    </div>
  );
}

